import Model from '@/store/plugins/orm/Model';
import { User, Table, Client, OrderItem, TaxRate, TaxItem, Product, ProductIngredient, Shift, CashRegister, AppEvent, KitchenTicket, KitchenTicketItem } from './ModelRepository';

import ApiService from '@/services/ApiService';
import CryptoService from '@/services/CryptoService';
import TenantService from '@/services/TenantService';
import OrderService from '@/services/OrderService';
import FiscalService from '@/services/FiscalService';
import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';
import EinvoiceService from '@/services/EinvoiceService';
import PrintService from '@/print/PrintService';

import i18n from '@/i18n';
import HashGen from '@/utilities/HashGen';
import PrintGen from '@/utilities/PrintGen';

import ErrorReporter from '@/wrappers/ErrorReporter';
import HtmlGen from '@/wrappers/HtmlGen';
import AppStorage from '@/wrappers/AppStorage';

const packageJson = require('/package.json');

export class Order extends Model {
    static get entity() {
        return 'orders';
    }

    static get primaryKey() {
        return 'hash';
    }

    static get STATUS_NEW() {
        return 'new';
    }
    static get STATUS_UNPAID() {
        return 'unpaid';
    }
    static get STATUS_PAID() {
        return 'paid';
    }
    static get STATUS_SUMMARY() {
        return 'summary';
    }
    static get STATUS_PROFORMA() {
        return 'proforma';
    }
    static get STATUS_ADVANCE() {
        return 'advance';
    }
    static get STATUS_CREDIT_NOTE() {
        return 'credit_note';
    }
    static get STATUS_COPY() {
        return 'copy';
    }
    static get STATUS_TRAINING() {
        return 'training';
    }

    static get REGISTER_TYPE_FISCAL() {
        return 'fiscal';
    }
    static get REGISTER_TYPE_EINVOICE() {
        return 'einvoice';
    }
    static get REGISTER_TYPE_MANUAL() {
        return 'manual';
    }
    static get REGISTER_TYPE_EXTERNAL() {
        return 'external';
    }

    static get SYNC_STATUS_SUCCESS() {
        return 'success';
    }
    static get SYNC_STATUS_SUCCESS_WITH_ERRORS() {
        return 'success_with_errors';
    }
    static get SYNC_STATUS_FAILED() {
        return 'failed';
    }

    static get DATE_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ssZ';
    }

    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.regular()),
            number: this.attr(null),
            internal_number: this.attr(null),
            shift_id: this.attr(null),
            shift_hash: this.attr(null),
            cash_register_id: this.attr(null, value => {
                let curCashRegister = CashRegister.current();
                return !value && curCashRegister ? curCashRegister.id : value;
            }),
            user_id: this.attr(null),
            table_id: this.attr(null),
            client_id: this.attr(null),
            status: this.attr(this.STATUS_NEW),
            paid_cash: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_credit_card: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_credit_card_business: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_invoice: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_advance: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_check: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_voucher: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_mobile_money: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            paid_other_noncash: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            change: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            tip: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            taxes: this.attr([], value => value ? value.map(item => new TaxItem(item)) : []),
            export: this.attr(false, value => !!value),
            last_activity_at: this.attr(null),
            iic: this.attr(null),
            fiscal_code: this.attr(null),
            register_type: this.attr(null),
            register_status: this.attr(null),
            register_number: this.attr(null),
            register_request: this.attr(null),
            register_response: this.attr(null),
            registered_at: this.attr(null),
            canceled_by_user_id: this.attr(null),
            canceled_at: this.attr(null),
            note: this.attr(null),
            due_date: this.attr(null),
            start_date: this.attr(null),
            end_date: this.attr(null),
            updated_at: this.attr(null),
            tax_period: this.attr(null),
            flag: this.attr(null),
            incoming: this.attr(false),

            items: this.attr([], value => value.map(item => new OrderItem(item))),
            refers_to: this.attr([], value => value.map(item => new Order(item))),
            referred_by: this.attr([], value => value.map(item => new Order(item))),
            reference: this.attr(null),

            shift: this.belongsTo(Shift, 'shift_hash'),
            cashRegister: this.belongsTo(CashRegister, 'cash_register_id'),
            user: this.belongsTo(User, 'user_id'),
            table: this.belongsTo(Table, 'table_id'),
            client: this.belongsTo(Client, 'client_id'),
            canceledByUser: this.belongsTo(User, 'canceled_by_user_id'),

            credit_note_referrals: this.attr([], value => value || []),
            sync_needed: this.attr(null),

            // TODO temporarily needed for compatibility
            is_einvoice: this.attr(false, value => !!value),
        };
    }

    /**
     * Get Order Statuses that are considered to be fiscal documents
     */
    static fiscalStatuses() {
        if (locale === 'me') {
            return [Order.STATUS_PAID, Order.STATUS_ADVANCE, Order.STATUS_UNPAID, Order.STATUS_SUMMARY, Order.STATUS_CREDIT_NOTE];
        } else if (locale === 'sr') {
            return [Order.STATUS_PAID, Order.STATUS_ADVANCE, Order.STATUS_PROFORMA, Order.STATUS_COPY, Order.STATUS_TRAINING];
        } else if (locale === 'ba') {
            return [Order.STATUS_PAID, Order.STATUS_ADVANCE, Order.STATUS_COPY];
        } else {
            return [];
        }
    }

    getRegisterRequestBa() {
        const transactionType = this.getToPay().toUnit() > 0 ? 'Sale' : 'Refund';

        let invoiceType = 'Normal';
        if (this.isProforma()) invoiceType = 'ProForma';
        else if (this.isCopy()) invoiceType = 'Copy';
        else if (this.isTraining()) invoiceType = 'Training';
        else if (this.isAdvance()) invoiceType = 'Advance';

        const user = this.getUser();
        const client = this.getClient();
        const payments = [];

        if (!this.getPaidCash().isZero()) {
            payments.push({
                amount: this.getPaidCash().add(this.getChange()).abs().toUnit(),
                paymentType: 'Cash'
            });
        }

        if (!this.getPaidCreditCard().isZero()) {
            payments.push({
                amount: this.getPaidCreditCard().abs().toUnit(),
                paymentType: 'Card'
            });
        }

        if (!this.getPaidInvoice().isZero()) {
            payments.push({
                amount: this.getPaidInvoice().abs().toUnit(),
                paymentType: 'WireTransfer'
            });
        }

        if (!this.getPaidCheck().isZero()) {
            payments.push({
                amount: this.getPaidCheck().abs().toUnit(),
                paymentType: 'Check'
            });
        }

        if (!this.getPaidVoucher().isZero()) {
            payments.push({
                amount: this.getPaidVoucher().abs().toUnit(),
                paymentType: 'Voucher'
            });
        }

        if (!this.getPaidMobileMoney().isZero()) {
            payments.push({
                amount: this.getPaidMobileMoney().abs().toUnit(),
                paymentType: 'MobileMoney'
            });
        }

        if (!this.getPaidOtherNoncash().isZero()) {
            payments.push({
                amount: this.getPaidOtherNoncash().abs().toUnit(),
                paymentType: 'Other'
            });
        }

        if (this.getPaidAdvance().toUnit() != 0 && this.getLeftToPay().toRoundedUnit(2) == 0) {
            payments.push({
                amount: 0,
                paymentType: 'Cash'
            });
        }

        const items = this.isSummary()
            ? this.paysForOrders()
                ? this.paysForOrders().flatMap(order => order.items)
                : []
            : this.items;

        const invoice = {
            requestId: this.hash,
            invoiceType: invoiceType,
            transactionType: transactionType,
            payment: payments,
            invoiceNumber: this.number + '',
            cashier: user?.name || '',
            items: items.map(i => {
                const taxRate = TaxRate.find(i.tax_rate_id);
                const item = {
                    code: i.getProduct()?.code || undefined,
                    name: i.name,
                    quantity: Math.abs(i.getCount()),
                    discount: i.getDiscountPercent(),
                    labels: [taxRate.label],
                    unitPrice: i.getUnitPrice().abs().toRoundedUnit(2),
                    totalAmount: i.getToPay().abs().toRoundedUnit(2)
                };

                if (i.gtin) {
                    item.gtin = i.gtin;
                }

                const unit = i.unit_of_measure
                if(unit) {
                    item.unit = i18n.t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                        ? i18n.t(`products.unit_of_measure.${unit}`)
                        : unit
                }

                if(i.comments.length) {
                    item.note = i.comments.join('\n');
                }

                return item;
            })
        };

        const formatToIsoString = (date) => {
            if(!date.isValid()) return null;
            return date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        }

        const advancePaidDateFlag = this.getFlag('advancePaidDate');
        if(invoice.invoiceType === 'Advance' && invoice.transactionType === 'Sale' && advancePaidDateFlag.length) {
            const date = formatToIsoString(moment(advancePaidDateFlag[0], 'YYYY-MM-DD HH:mm:ss'));
            if(date) {
                invoice.dateAndTimeOfIssue = date;
            }
        }

        const refOrder = this.refers_to.length ? this.refers_to[0] : null;
        if (refOrder) {
            if(refOrder.register_number && refOrder.registered_at) {
                invoice.referentDocumentNumber = refOrder.register_number;
                invoice.referentDocumentDT = formatToIsoString(moment.utc(refOrder.registered_at).tz('Europe/Sarajevo'));
            }
        } else if(this.flagsInclude('ref')) {
            const reference = this.getFlag('ref');
            invoice.referentDocumentNumber = reference[0];
            if(reference[1]) {
                invoice.referentDocumentDT = formatToIsoString(moment.tz(reference[1], 'DD.MM.YYYY. HH:mm:ss', 'Europe/Sarajevo'));
            }
        }

        if (user && user.name) {
            invoice.cashier = user.name;
        }

        if (client) {
            invoice.buyer = {
                idNumber: client.id_number,
                name: client.name,
                address: client.address,
                postalNumber: client.postal_number,
                city: client.city,
            }
        }

        return invoice;
    }

    getRegisterRequestSr() {
        const transactionType = this.getToPay().toUnit() > 0 ? 'Sale' : 'Refund';

        let invoiceType = 'Normal';
        if (this.isProforma()) invoiceType = 'ProForma';
        else if (this.isCopy()) invoiceType = 'Copy';
        else if (this.isTraining()) invoiceType = 'Training';
        else if (this.isAdvance()) invoiceType = 'Advance';

        const user = this.getUser();
        const client = this.getClient();
        const payments = [];

        if (!this.getPaidCash().isZero()) {
            payments.push({
                amount: this.getPaidCash().add(this.getChange()).abs().toUnit(),
                paymentType: 'Cash'
            });
        }

        if (!this.getPaidCreditCard().isZero()) {
            payments.push({
                amount: this.getPaidCreditCard().abs().toUnit(),
                paymentType: 'Card'
            });
        }

        if (!this.getPaidInvoice().isZero()) {
            payments.push({
                amount: this.getPaidInvoice().abs().toUnit(),
                paymentType: 'WireTransfer'
            });
        }

        if (!this.getPaidCheck().isZero()) {
            payments.push({
                amount: this.getPaidCheck().abs().toUnit(),
                paymentType: 'Check'
            });
        }

        if (!this.getPaidVoucher().isZero()) {
            payments.push({
                amount: this.getPaidVoucher().abs().toUnit(),
                paymentType: 'Voucher'
            });
        }

        if (!this.getPaidMobileMoney().isZero()) {
            payments.push({
                amount: this.getPaidMobileMoney().abs().toUnit(),
                paymentType: 'MobileMoney'
            });
        }

        if (!this.getPaidOtherNoncash().isZero()) {
            payments.push({
                amount: this.getPaidOtherNoncash().abs().toUnit(),
                paymentType: 'Other'
            });
        }

        if (this.getPaidAdvance().toUnit() != 0 && this.getLeftToPay().toRoundedUnit(2) == 0) {
            payments.push({
                amount: 0,
                paymentType: 'Cash'
            });
        }

        const items = this.isSummary()
            ? this.paysForOrders()
                ? this.paysForOrders().flatMap(order => order.items)
                : []
            : this.items;

        const invoice = {
            requestId: this.hash,
            invoiceType: invoiceType,
            transactionType: transactionType,
            payment: payments,
            invoiceNumber: (isAllegra ? '793/' : '794/') + packageJson.certifiedVersion,
            options: {
                OmitTextualRepresentation: 0,
                OmitQRCodeGen: 1
            },
            items: items.map(i => {
                const taxRate = TaxRate.find(i.tax_rate_id);
                const namePrefix = this.getFlag('type').includes('noCompensation') ? '00: ' : ''
                const item = {
                    name: namePrefix + i.name,
                    quantity: Math.abs(i.getCount()),
                    discount: i.getDiscountPercent(),
                    labels: [taxRate.label],
                    unitPrice: i.getUnitPriceAfterDiscount().abs().toRoundedUnit(2),
                    totalAmount: i.getToPay().abs().toRoundedUnit(2)
                };

                if (i.gtin) {
                    item.gtin = i.gtin;
                }

                const unit = i.unit_of_measure
                if(unit) {
                    item.unit = i18n.t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                        ? i18n.t(`products.unit_of_measure.${unit}`)
                        : unit
                }

                if(i.comments.length) {
                    item.note = i.comments.join('\n');
                }

                return item;
            })
        };

        const formatToIsoString = (date) => {
            if(!date.isValid()) return null;
            return date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        }

        const advancePaidDateFlag = this.getFlag('advancePaidDate');
        if(invoice.invoiceType === 'Advance' && invoice.transactionType === 'Sale' && advancePaidDateFlag.length) {
            const date = formatToIsoString(moment(advancePaidDateFlag[0], 'YYYY-MM-DD HH:mm:ss'));
            if(date) {
                invoice.dateAndTimeOfIssue = date;
            }
        }

        const refOrder = this.refers_to.length ? this.refers_to[0] : null;
        if (refOrder) {
            if(refOrder.register_number && refOrder.registered_at) {
                invoice.referentDocumentNumber = refOrder.register_number;
                invoice.referentDocumentDT = formatToIsoString(moment.utc(refOrder.registered_at).tz('Europe/Belgrade'));
            }
        } else if(this.flagsInclude('ref')) {
            const reference = this.getFlag('ref');
            invoice.referentDocumentNumber = reference[0];
            if(reference[1]) {
                invoice.referentDocumentDT = formatToIsoString(moment.tz(reference[1], 'DD.MM.YYYY. HH:mm:ss', 'Europe/Belgrade'));
            }
        }

        if (user && user.name) {
            invoice.cashier = user.name;
        }

        if(transactionType === 'Refund' && this.getFlag('reason')[0] === 'error') {
            const tenant = this.$store().getters.tenant;
            invoice.buyerId = `10:${tenant.venue.tax_id_number}`
        } else if (client) {
            invoice.buyerId = client.getFullIdNumber();
            if (client.cost_center && ['Normal', 'Advance'].includes(invoice.invoiceType) && invoice.transactionType === 'Sale') {
                invoice.buyerCostCenterId = client.cost_center;
            }
        }

        return invoice;
    }

    shouldRegister() {
        return (locale === 'sr' || locale === 'ba') &&
            this.register_status !== 'success' &&
            Order.fiscalStatuses().includes(this.status) &&
            this.isFiscal() &&
            !EinvoiceService.einvoiceOnly()
    }

    async register() {
        if (!this.shouldRegister()) {
            console.log('Order register skipped ' + this.hash)
            return { success: true }
        }

        console.log('Order register ' + this.hash)

        if(locale === 'ba') {
            const request = this.getRegisterRequestBa();
            this.register_request = request;

            const response = await FiscalServiceLegacy.createInvoice(request);
            return this.handleRegisterResponseBa(request, response);
        }

        if(locale === 'sr') {
            const request = this.getRegisterRequestSr();
            this.register_request = request;

            const response = await FiscalService.createInvoice(request);
            return this.handleRegisterResponseSr(request, response);
        }

        return {
            success: false,
            error: i18n.t('general.action_failed'),
        }
    }

    async handleRegisterResponseBa(request, response) {
        if(!response.success) {
            const error = response.error?.code === 'exception'
                ? i18n.t('order.error.register.exception')
                : response.error?.message;

            // Report the register error
            console.log('Order register error (legacy)', response)

            ErrorReporter.reportMessage('Order register error (legacy)', {
                request: JSON.stringify(request),
                response: JSON.stringify(response),
                orderHash: this.hash,
                error,
            })

            return { success: false, error }
        }

        this.register_status = 'success';
        this.register_response = response.data;
        this.registered_at = this.last_activity_at
        this.register_number = this.number;

        return { success: true }
    }

    async handleRegisterResponseSr(request, response) {
        if (!response.success) {
            let error = response.error?.code === 'exception'
                ? i18n.t('order.error.register.exception')
                : response.error?.message;

            if(!error && !!response.data?.modelState) {
                error = i18n.t('sdc.status.receipt_data_not_valid')
            }

            // Report the register error
            console.log('Order register error', response)
            ErrorReporter.reportMessage('Order register error', {
                request: JSON.stringify(request),
                response: JSON.stringify(response),
                orderHash: this.hash,
                error,
            });

            // If request to SDC timed out, give it some more and try to fetch the registered receipt from it
            if(/timeout of .*ms exceeded/.test(response.error?.message)) {
                response.data = '4242';
                const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
                await delay(15000);
            }

            if(response.data != '4242') return { success: false, error };

            const existingOrder = Order.all().find(o => o.hash === this.hash);

            // Receipt exists in SDC and is saved in store
            if(existingOrder?.register_status === 'success') {
                ErrorReporter.reportMessage('Order register error: already_exists_pos', { orderHash: this.hash });

                this.$fill(existingOrder.$toJson());
                return { success: true };
            }

            // Receipt exists in SDC and but is NOT saved in store
            ErrorReporter.reportMessage('Order register error: already_exists_sdc', { orderHash: this.hash });

            const existingRegisterResponse = await FiscalService.getInvoice(this.hash);
            if(!existingRegisterResponse.success) return { success: false, error };

            response = existingRegisterResponse;
        }

        this.register_status = 'success';
        this.register_response = response.data;

        if(this.refersToAdvance()) {
            const advanceRefOrder = this.referredAdvanceOrders()[0];
            this.register_response.advancePaid = this.getPaidAdvance().toRoundedUnit(4);
            this.register_response.advanceTax = advanceRefOrder.getTax().abs().toRoundedUnit(4);
        }

        if(this.correctsOrders()[0]?.getPaidAdvance().toUnit()) {
            this.register_response.advancePaid = this.correctsOrders()[0].register_response.advancePaid
            this.register_response.advanceTax = this.correctsOrders()[0].register_response.advanceTax
        }

        this.registered_at = moment(response.data.sdcDateTime).utc().format(Order.DATE_FORMAT);
        this.register_number = response.data.invoiceNumber;

        delete this.register_response.verificationQRCode;
        delete this.register_response.encryptedInternalData;
        delete this.register_response.signature;
        delete this.register_response.journal;

        return { success: true }
    }

    async sync(newOrder = true) {
        if(this.isEinvoice()) {
            return this.einvoiceSend();
        }

        if ((locale === 'sr' || locale === 'ba') && newOrder) {
            const registerResult = await this.register();

            if (!registerResult.success) {
                ErrorReporter.timeLog('receipt', 'SYNC REGISTER ERROR')
                return { status: Order.SYNC_STATUS_FAILED, error: registerResult.error };
            }

            ErrorReporter.timeLog('receipt', 'SYNC REGISTER SUCCESS')
        }

        if (!this.$store().getters.isOnline) {
            this.saveToNative();
            this.$save();
            if(newOrder) {
                this.onSave();
            }
            return { status: Order.SYNC_STATUS_SUCCESS };
        }

        const timeLimit = 2000;
        const defaultResult = { status: Order.SYNC_STATUS_SUCCESS, default: true };
        const promise1 = new Promise(resolve => setTimeout(() => resolve(defaultResult), timeLimit));
        const promise2 = this.syncExec(newOrder);

        const syncResult = await Promise.race([promise1, promise2]);

        if(syncResult.default) {
            delete syncResult.default;
            this.setFlag('timeout', 'true');
            ErrorReporter.reportMessage(`Order post longer than ${timeLimit}ms`);
        }

        return syncResult;
    }

    async syncExec(newOrder = true) {
        try {
            const url = isAllegra ? '/allegra/orders' : '/orders';
            const response = await ApiService.post(url, { order: this.$toJson() });

            const orderTimedOut = !!this.getFlag('timeout')[0]

            // if order was returned we need to make sure it's stored on the client
            // if the returned order is not the this one, than this one needs to be dropped
            if (response.data.order) {
                AppEvent.build('orderUpdate', {
                    order: response.data.order?.id,
                    ref_orders: response.data.ref_orders?.map(o => o.id) || [],
                    product_counts: response.data.product_counts || [],
                    client: response.data.client?.id
                }).send()

                if (response.data.order.hash === this.hash) {
                    this.$fill(response.data.order);
                }

                if (this.number) {
                    OrderService.setLatestNumber(this.number, true);
                }

                Order.insert({ data: [response.data.order], freeze: true });
            }

            if (response.data.ref_orders && response.data.ref_orders.length) {
                Order.insert({ data: response.data.ref_orders, freeze: true });
            }

            if (response.data.client) {
                Client.update(response.data.client);
            }

            if (response.data.product_counts && response.data.product_counts.length) {
                Product.update(response.data.product_counts);
            }

            const nonBlockingError = response.data.error &&
                (response.data.register_status === 'failed' || ['already_exists', 'duplicate'].includes(response.data.error));

            if (nonBlockingError) {
                return {
                    status: Order.SYNC_STATUS_SUCCESS_WITH_ERRORS,
                    efiError: i18n.t(`order.error.${response.data.error}`)
                };
            }

            if (response.data.error) {
                if(orderTimedOut || locale === 'sr') {
                    this.saveToNative();
                    ErrorReporter.reportMessage('Order lost' + (orderTimedOut ? ': timeout' : ''), { order: this.jsonToLog() });
                }

                return { status: Order.SYNC_STATUS_FAILED, error: i18n.t(`order.error.${response.data.error}`) };
            }

            return { status: Order.SYNC_STATUS_SUCCESS };
        } catch (error) {
            this.saveToNative();
            this.$save();
            if(newOrder) {
                this.onSave();
            }

            console.log(error.message);

            ErrorReporter.reportError(error, { order: this.jsonToLog() });

            const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
            await delay(5000);
            const isOnline = await this.$store().dispatch('connectionCheck');

            if (isOnline) {
                return { status: Order.SYNC_STATUS_SUCCESS };
            }

            return {
                status: Order.SYNC_STATUS_SUCCESS_WITH_ERRORS,
                error: i18n.t('order.error.order_issue_failed')
            };
        }
    }

    async saveToNative() {
        const ordersJson = await AppStorage.native().getItem('orders');
        const orders = ordersJson ? JSON.parse(ordersJson) : [];
        orders.push(this.$duplicate());
        AppStorage.native().setItem('orders', JSON.stringify(orders));
    }

    jsonToLog() {
        const json = this.$toJson();
        json.items = JSON.stringify(json.items);
        json.refers_to = JSON.stringify(json.refers_to);
        json.referred_by = JSON.stringify(json.referred_by);
        json.taxes = JSON.stringify(json.taxes);
        return json;
    }

    static async onOrdersSync(event) {
        const deletedRecords = event.data.items.filter(i => i.action === 'delete').map(i => i.record)
        const updatedRecords = event.data.items.filter(i => i.action !== 'delete').map(i => i.record)

        if(updatedRecords.length) {
            Order.insert({ data: updatedRecords })
        }

        deletedRecords.forEach(r => {
            const record = Order.find(r.hash)
            record.$delete()
        })
    }

    /**
     * Fetch new orders from server
     */
    static async fetchLatest() {
        const lastUpdatedAt = Order.getLastUpdatedAt();
        const params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        const response = await ApiService.get('/orders/latest', { params: params });
        Order.insert({ data: response.data.orders, freeze: true });
    }

    /**
     * Create a new order instance
     */
    static createNew(user = null, table = null) {
        const curShift = Shift.current();

        return new Order({
            hash: HashGen.regular(),
            number: OrderService.getNextNumber(),
            shift_id: curShift ? curShift.id : null,
            shift_hash: curShift ? curShift.hash : null,
            user_id: user ? user.id : null,
            table_id: table ? table.id : null
        });
    }

    /**
     * Create a new order instance from advance order
     */
    static createFromAdvance(orders, options = {}) {
        const { user, anotherAdvance = false } = options;

        if (!Array.isArray(orders)) {
            orders = [orders];
        }

        const order = orders[0];

        if(anotherAdvance) {
            const freshOrder = Order.createNew(user);
            const registeredAt = moment
                .utc(order.registered_at)
                .tz('Europe/Belgrade')
                .format('DD.MM.YYYY. HH:mm:ss')

            if(order.client_id) {
                freshOrder.client_id = order.client_id;
                const client = order.getClient();
                freshOrder.client = client;
            }

            freshOrder.setFlag('ref', order.register_number, registeredAt);
            return freshOrder;
        }

        const freshOrder = order.fresh(this.user, false, locale === 'sr');
        orders.forEach(o => freshOrder.addReference(o, 'advance'));

        if(order.client_id) {
            freshOrder.client_id = order.client_id;
            const client = order.getClient();
            freshOrder.client = client;
        }

        freshOrder.items = [];
        freshOrder.paid_advance = orders.reduce((acc, cur) => acc.add(cur.getRemainingAdvanceAmount().abs()), Money.parse(0)).toFormat('0.0000');
        freshOrder.taxes = Order.calculateTaxes(freshOrder);

        return freshOrder;
    }

    /**
     * Make a duplicate of the current order.
     */
    $duplicate() {
        return new Order(this.$toJson());
    }

    /**
     * Create a fresh copy of order
     */
    fresh(user = null, copyPayments = false, reverse = false) {
        let freshOrder = Order.createNew(user);

        freshOrder.client_id = this.client_id;
        freshOrder.table_id = this.table_id;
        freshOrder.export = this.export;
        freshOrder.note = this.note;
        freshOrder.due_date = this.due_date;
        freshOrder.start_date = this.start_date;
        freshOrder.end_date = this.end_date;
        freshOrder.tax_period = this.tax_period;
        freshOrder.register_type = this.register_type;
        freshOrder.taxes = this.taxes;
        freshOrder.items = this.items.map(item => item.fresh(freshOrder, reverse));

        freshOrder.is_einvoice = this.is_einvoice;

        if (copyPayments) {
            const multiplicator = !reverse ? 1 : -1;

            freshOrder.paid_cash = this.getPaidCash().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_credit_card = this.getPaidCreditCard().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_invoice = this.getPaidInvoice().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_credit_card_business = this.getPaidCreditCardBusiness().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_advance = this.getPaidAdvance().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_check = this.getPaidCheck().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_voucher = this.getPaidVoucher().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_mobile_money = this.getPaidMobileMoney().multiply(multiplicator).toFormat('0.0000');
            freshOrder.paid_other_noncash = this.getPaidOtherNoncash().multiply(multiplicator).toFormat('0.0000');

            if(!reverse) {
                freshOrder.change = this.change;
            }
        }

        return freshOrder;
    }

    /**
     * Create corrective order for this order
     */
    createCorrectiveOrder(user = null, type = 'cancel', correctedItems = [], paymentType = null) {
        const curShift = Shift.current();

        const correctiveOrder = new Order({
            hash: HashGen.regular(),
            number: OrderService.getNextNumber(),
            shift_id: curShift ? curShift.id : null,
            shift_hash: curShift ? curShift.hash : null,
            user_id: user ? user.id : null,
            table_id: this.table_id,
            client_id: this.client_id,
            export: this.export,
            register_type: this.register_type,
            is_einvoice: this.is_einvoice,
        });

        const skipDebt = this.getFlag('debt').includes('skip');
        if(skipDebt) {
            correctiveOrder.setFlag('debt', 'skip');
        }

        const noCompensation = this.getFlag('type').includes('noCompensation');
        if(noCompensation) {
            correctiveOrder.setFlag('type', 'noCompensation');
        }

        if (type === 'cancel') {
            correctiveOrder.items = this.items.map(orderItem => orderItem.fresh(correctiveOrder, true));

            correctiveOrder.paid_cash = this.getPaidCash().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_credit_card = this.getPaidCreditCard().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_invoice = this.getPaidInvoice().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_credit_card_business = this.getPaidCreditCardBusiness().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_advance = this.getPaidAdvance().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_check = this.getPaidCheck().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_voucher = this.getPaidVoucher().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_mobile_money = this.getPaidMobileMoney().multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_other_noncash = this.getPaidOtherNoncash().multiply(-1).toFormat('0.0000');

            correctiveOrder.tax_period = this.tax_period;
        }

        if (type === 'correct') {
            correctiveOrder.items = correctedItems.map(orderItem => orderItem.fresh(correctiveOrder));

            if(paymentType) {
                correctiveOrder.setPaid(paymentType);
            }
        }

        if (type === 'correctSummary') {
            correctiveOrder.internal_number = this.internal_number;
            correctiveOrder.taxes = this.taxes;
            correctiveOrder.note = this.note;
            correctiveOrder.due_date = this.due_date;
            correctiveOrder.start_date = this.start_date;
            correctiveOrder.end_date = this.end_date;
            correctiveOrder.refers_to = this.refers_to;

            correctiveOrder.items = this.items.map(item => item.fresh(correctiveOrder));
            correctiveOrder.setPaid(paymentType);
        }

        correctiveOrder.addReference(this, 'corrective');

        return correctiveOrder;
    }

    /**
     * Close the order. No further changes should be done after order is closed.
     */
    close(options) {
        const { user, status, paidOrders = [], skipTaxesCalculation = false, registerType = null } = options;

        this.user_id = user.id;
        this.status = this.$store().getters.trainingMode && (status === Order.STATUS_PAID || status === Order.STATUS_ADVANCE)
                ? Order.STATUS_TRAINING
                : status;
        this.last_activity_at = moment().utc().format(Order.DATE_FORMAT);
        this.sync_needed = this.$store().getters.tenant.venue.hash;

        // Set order number
        if (this.shouldHaveNumber()) {
            this.number = OrderService.setLatestNumber(this.number);

            // Set order internal number (for invoices)
            if (this.internal_number) {
                OrderService.setLatestInternalNumber(this.internal_number);
            }
        } else {
            this.number = null
        }

        this.setRegisterType(registerType);

        if (skipTaxesCalculation) return;

        this.taxes = !paidOrders || !paidOrders.length ? Order.calculateTaxes(this) : Order.calculateTaxes(paidOrders);
    }

    /**
     * When order is saved to the local store update product counts and other related items
     */
    onSave() {
        // Add reverse references for referred orders
        const referredOrders = [];
        this.refers_to.forEach(order => {
            order.addReference(this, order.reference.type, true);
            referredOrders.push({ hash: order.hash, referred_by: order.referred_by })
        })

        if(referredOrders.length) {
            Order.update(referredOrders);
        }

        // Subtract sold product counts from the stock
        const productCounts = this.getUpdatedProductCounts();
        if (productCounts.length) {
            Product.update(productCounts);
        }

        // If there is cash exchanged update client debt
        if (this.isCashExchanged() && locale === 'me') {
            const client = this.getClient();
            if (this.flagsInclude('installments') && client) {
                const debt = client
                    .getDebt()
                    .add(this.getPaidOtherNoncash())
                    .toFormat('0.0000');
                Client.update({ id: this.client_id, debt: debt });
            }
        }
    }

    /**
     * Calculate the new product counts after the items are sold
     */
    getUpdatedProductCounts() {
        // if order does not actually sell items return empty array
        if(locale === 'me') {
            if (!this.isPaid() && !this.isCreditNote() && !this.isUnpaid()) return [];
        } else {
            if (!this.isPaid() && !this.isCreditNote()) return [];
        }

        const productCounts = [];

        this.items.forEach(orderItem => {
            const product = orderItem.getProduct();
            const count = !this.isCreditNote() ? orderItem.getCount() : orderItem.getAdditionalCount();

            if (product && !product.is_made) {
                const alreadyAdded = productCounts.find(p => p.id === product.id);

                if (alreadyAdded) {
                    alreadyAdded.count -= count;
                } else {
                    productCounts.push({
                        id: product.id,
                        count: product.getCount() - count
                    });
                }
            }

            if (product && product.is_made) {
                ProductIngredient.all()
                    .filter(ingredient => ingredient.super_product_id == product.id)
                    .forEach(ingredient => {
                        const ingredientProduct = Product.all().find(p => p.id == ingredient.product_id);

                        if (ingredientProduct) {
                            const alreadyAdded = productCounts.find(p => p.id === ingredientProduct.id);

                            if (alreadyAdded) {
                                alreadyAdded.count -= count * ingredient.getQuantity();
                            } else {
                                productCounts.push({
                                    id: ingredientProduct.id,
                                    count: ingredientProduct.getCount() - count * ingredient.getQuantity()
                                });
                            }
                        }
                    });
            }
        });

        return productCounts;
    }

    /**
     * Set order as paid
     */
    setPaid(type, value = null) {
        const payments = {
            cash: Money.parse(0),
            credit_card: Money.parse(0),
            invoice: Money.parse(0),
            credit_card_business: Money.parse(0),
            advance: this.getPaidAdvanceEffective(),
            check: Money.parse(0),
            voucher: Money.parse(0),
            mobile_money: Money.parse(0),
            other_noncash: Money.parse(0),
        }

        let change = Money.parse(0);
        let tip = Money.parse(0);

        if (type === 'cash') {
            payments.cash = this.getLeftToPay();

            if (value) {
                const skipDebt = this.getFlag('debt').includes('skip');
                if(skipDebt) {
                    tip = Money.parse(value).subtract(payments.cash);
                } else {
                    change = Money.parse(value).subtract(payments.cash);
                }
            }
        } else if (type === 'credit_card') {
            if (!value) {
                payments.credit_card = this.getLeftToPay();
            } else if (value > this.getLeftToPay().toRoundedUnit(2)) {
                payments.credit_card = this.getLeftToPay();
                tip = Money.parse(value).subtract(payments.credit_card);
            } else {
                payments.cash = Money.parse(value)
                payments.credit_card = this.getLeftToPay().subtract(payments.cash);

                // correct if the rounding caused the paid amount to be negative
                if (payments.credit_card.toUnit() < 0) {
                    payments.cash = this.getLeftToPay();
                    payments.credit_card = Money.parse(0);
                }
            }
        } else if (type === 'other_noncash') {
            if (!value) {
                payments.other_noncash = this.getLeftToPay();
            } else {
                payments.cash = Money.parse(value);
                payments.other_noncash = this.getLeftToPay().subtract(payments.cash);

                // correct if the rounding caused the paid amount to be negative
                if (payments.other_noncash.toUnit() < 0) {
                    payments.cash = this.getLeftToPay();
                    payments.other_noncash = Money.parse(0);
                }
            }
        } else if(type === 'mixed_payment' || type === 'advance') {
            let leftToPay = this.getLeftToPay();
            value.forEach((payment, index) => {
                if(index === value.length - 1) {
                    payments[payment.type] = leftToPay;
                } else {
                    const amount = Money.parse(payment.amount);
                    payments[payment.type] = amount;
                    leftToPay = leftToPay.subtract(amount)
                }
            })
        } else {
            payments[type] = this.getLeftToPay();
        }

        Object.keys(payments).forEach(type => {
            this[`paid_${type}`] = payments[type].toFormat('0.0000');
        })

        this.change = change.toFormat('0.0000');
        this.tip = tip.toFormat('0.0000');
    }

    /**
     * Check if the order action can be performed.
     */
    async validateOrderAction(action, user, payment = null) {
        const allowedActions = !this.isIncoming()
        ? [
            'issue',
            'pay',
            'advance',
            'copy',
            'correct',
            'cancel',
            'correctSummary',
            'proforma',
            'proformaPay',
            'closeAdvance',
            'einvoice',
            'einvoiceAdvance',
            'einvoiceStorno',
            'einvoiceApprove',
            'einvoiceReject',
        ] : [
            'einvoiceApprove',
            'einvoiceReject',
        ]

        const tenant = this.$store().getters.tenant
        if(locale === 'me' && tenant?.cert_expire_at && moment(tenant.cert_expire_at).diff(moment(), 'days') <= -2) {
            return { success: false, error: i18n.t('general.certificate_expired', { value: moment(tenant.cert_expire_at).format('DD.MM.YYYY.') }) };
        }

        if(!allowedActions.includes(action)) return { success: false, error: i18n.t('general.action_failed') };

        if (!user.receipts_allowed) {
            return { success: false, error: i18n.t('order.error.operator_code') };
        }

        const lastOrderDate = OrderService.getLatestOrderDate();
        if(lastOrderDate && moment.utc(lastOrderDate).diff(moment().utc(), 'minutes') > 120) {
            return { success: false, error: i18n.t('general.time_not_valid') };
        }

        const isEinvoice = action.includes('einvoice');
        if(isEinvoice) {
            if (!this.$store().getters.isOnline) {
                return { success: false, error: i18n.t('connection.internet_offline_message_not_allowed') };
            }

            if(!EinvoiceService.isEnabled()) {
                return { success: false, error: i18n.t('einvoice.error.not_enabled') };
            }
        }

        const shouldRegisterSr = locale === 'sr' &&
            !this.isUnpaid() &&
            ['pay', 'advance', 'copy', 'correct', 'cancel', 'proforma', 'proformaPay', 'closeAdvance'].includes(action) &&
            !EinvoiceService.einvoiceOnly() &&
            !isEinvoice;

        if (shouldRegisterSr) {
            const sdcStatus = await FiscalService.checkSdcStatus();
            if (sdcStatus.error) {
                console.log('SDC error: ' + sdcStatus.error.message);
                return { success: false, error: sdcStatus.error.message };
            }
        }

        const hasItemsWithZeroPrice = this.items.some(item => item.getUnitPrice().toUnit() === 0);
        const hasItemsWithNegativeCount = this.items.some(item => item.getCount() < 0);
        const allItemsHaveNegativeCounts = this.items.every(item => item.getCount() < 0);

        if(hasItemsWithZeroPrice) {
            return { success: false, error: i18n.t('general.action_failed') };
        }

        if(hasItemsWithNegativeCount && (!allItemsHaveNegativeCounts || !['copy', 'pay', 'advance', 'closeAdvance'].includes(action))) {
            return { success: false, error: i18n.t('general.action_failed') };
        }

        if(hasItemsWithNegativeCount && isEinvoice) {
            return { success: false, error: i18n.t('general.action_failed') };
        }

        if(action == 'pay' || action == 'advance') {
            if(hasItemsWithNegativeCount && !this.flagsInclude('ref')) {
                return { success: false, error: i18n.t('order.error.reference_not_present') };
            }

            let curCashRegister = CashRegister.current();
            if(!curCashRegister.allowsOrderPayment(payment.type, payment.value)) {
                return { success: false, error: i18n.t('order.error.payment_type_not_allowed') };
            }
        }

        if(locale === 'sr' && (action == 'advance' || action == 'einvoiceAdvance')) {
            const hasItemsWithInvalidQuantities = this.items.some(item => Math.abs(item.getCount()) !== 1);
            if(hasItemsWithInvalidQuantities) {
                return { success: false, error: i18n.t('order.error.item_count_advance') };
            }
        }

        // If this receipt is a final receipt that closes the advance
        if (this.refersToAdvance()) {
            const advanceRefOrders = this.referredAdvanceOrders();

            const mustBeEinvoice = advanceRefOrders[0].isEinvoice();
            if(!isEinvoice && mustBeEinvoice) {
                return { success: false, error: i18n.t('order.error.advance_final_must_be_einvoice') };
            }

            const availableAdvanceAmount = advanceRefOrders.reduce((acc, cur) => acc.add(cur.getRemainingAdvanceAmount().abs()), Money.parse(0));

            // final order must have the same tax items as the referred advance orders
            const advanceTaxAmount = advanceRefOrders.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
            const uniqueAdvanceRefOrderTaxRates = advanceRefOrders.flatMap(o => o.taxes).map(t => t.id)
                .filter((t, index, array) => index === array.indexOf(t));
            const uniqueTaxRates = this.taxes.map(t => t.id)
                .filter((t, index, array) => index === array.indexOf(t));
            const someTaxRatesDoNotExist = uniqueAdvanceRefOrderTaxRates.some(t => !uniqueTaxRates.includes(t));

            if(someTaxRatesDoNotExist && advanceTaxAmount.toUnit() !== 0) {
                return { success: false, error: i18n.t('order.error.advance_tax_rates_mismatch') };
            }

            // TODO uncomment and test
            // const availableAdvanceTaxes = advanceRefOrders.flatMap(o => o.getRemainingTaxes()).map(t => {
            //     const taxItem = t.$duplicate();
            //     taxItem.price_before_tax = Money.parse(taxItem.price_before_tax).multiply(-1).toFormat('0.0000');
            //     taxItem.value = Money.parse(taxItem.value).multiply(-1).toFormat('0.0000');
            //     return taxItem;
            // });
            // const resultingTaxes = TaxItem.merge([...this.taxes, ...availableAdvanceTaxes])

            // if final receipt amount is less than available advance amount
            if(availableAdvanceAmount.toUnit() > this.getPaidAdvanceEffective().toUnit()) {

                // TODO remove - temporarily disabled larger advance for e-invoices
                if(this.isEinvoice()) {
                    return { success: false, error: i18n.t('order.error.advance_larger_than_expected') };
                }

                // must only have one large advance to refer to and that advance must not be refund
                if(advanceRefOrders.length > 1 || advanceRefOrders[0].getToPay().toUnit() < 0) {
                    // TODO check tax items
                    return { success: false, error: i18n.t('order.error.advance_smaller_than_expected') };
                }

                // TODO uncomment and test
                // const taxesMoreThanAdvance = resultingTaxes.some(t => {
                //     return parseFloat(t.value) >= 0 || parseFloat(t.price_before_tax) >= 0;
                // })

                // if(taxesMoreThanAdvance) {
                //     return { success: false, error: i18n.t('order.error.advance_taxes_smaller_than_expected') };
                // }

                if(advanceRefOrders[0].isMixedPayment()) {
                    return { success: false, error: i18n.t('order.error.advance_mixed_payment') };
                }

                const uniqueTaxRates = this.items.map(i => i.tax_rate)
                    .filter((rate, index, array) => array.indexOf(rate) == index);

                const advanceUniqueTaxRates = advanceRefOrders[0].items.map(i => i.tax_rate)
                    .filter((rate, index, array) => array.indexOf(rate) == index);

                if(uniqueTaxRates.length !== 1 || advanceUniqueTaxRates.length !== 1) {
                    return { success: false, error: i18n.t('order.error.advance_single_tax_rate') };
                }

                if(uniqueTaxRates[0] != advanceUniqueTaxRates[0]) {
                    return { success: false,
                        error: i18n.t('order.error.advance_tax_rate_mismatch', {
                            value1: parseFloat(uniqueTaxRates[0]) + '%',
                            value2: parseFloat(advanceUniqueTaxRates[0]) + '%'
                        })
                    };
                }
            }

            // if final receipt amount is equal or greater than available advance amount
            // TODO uncomment and test
            // else {
                // const taxesLessThanAdvance = resultingTaxes.some(t => {
                //     return parseFloat(t.value) < 0 || parseFloat(t.price_before_tax) < 0;
                // })

                // if(taxesLessThanAdvance) {
                //     return { success: false, error: i18n.t('order.error.advance_taxes_larger_than_expected') };
                // }
            // }
        }

        if (action == 'correct') {
            if ((!this.isPaid() && !(locale === 'me' && this.isUnpaid())) || this.isMixedPayment() || this.isEinvoice()) {
                return { success: false, error: i18n.t('general.action_failed') };
            }
        }

        if (action == 'cancel') {
            if (this.isNew() || (this.isProforma() && locale !== 'sr') || (this.isEinvoice() && !this.einvoiceIsSent() && !this.einvoiceIsApproved())) {
                return { success: false, error: i18n.t('general.action_failed') };
            }
        }

        if (action == 'correctSummary') {
            if (this.isNew() || !this.isSummary()) {
                return { success: false, error: i18n.t('general.action_failed') };
            }
        }

        if (action == 'einvoiceReject') {
            if ((!this.einvoiceIsNew() && !this.einvoiceIsSeen()) || !this.isIncoming()) {
                return { success: false, error: i18n.t('general.action_failed') };
            }
        }

        if (action == 'einvoiceApprove') {
            if ((!this.einvoiceIsNew() && !this.einvoiceIsSeen() && !this.einvoiceIsRejected()) || !this.isIncoming()) {
                return { success: false, error: i18n.t('general.action_failed') };
            }
        }

        if (action == 'einvoiceStorno') {
            if (!this.isEinvoice() || this.isIncoming()) {
                return { success: false, error: i18n.t('general.action_failed') };
            }
        }

        return { success: true };
    }

    /**
     * Issue order. Will persist the changes
     * to the store, and sync with the server.
     */
    async issue(options = {}) {
        const { user, choosePrinter, skipPrint } = options;

        const validateResult = await this.validateOrderAction('issue', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        this.close({ user, status: Order.STATUS_UNPAID });

        const result = await this.sync();

        if (!skipPrint && result.status !== Order.SYNC_STATUS_FAILED) {
            let delay = 0
            if (locale === 'me') {
                delay = 2000
                const printResult = await this.printReceipt({ choosePrinter });

                if (!printResult.success) {
                    result.printError = printResult.errorCode
                        ? i18n.t(`print.${printResult.errorCode}`)
                        : printResult.error;
                    result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
                }
            }

            // print order
            const spilitReceiptActive = !!this.$store().getters.splitReceipt?.active
            if(!spilitReceiptActive) {
                setTimeout(() => this.print(), delay);
                KitchenTicket.issue(this)
            }
        }

        return { ...result, order: this };
    }

    /**
     * Issue a copy of the order. Will persist the changes
     * to the store, and sync with the server.
     */
    async copy(options = {}) {
        const { user, choosePrinter, forceInvoiceForm } = options;

        const validateResult = await this.validateOrderAction('copy', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        const freshOrder = this.fresh(user, true);
        freshOrder.addReference(this, 'copy');

        freshOrder.close({
            user,
            status: Order.STATUS_COPY,
            paidOrders: [],
            skipTaxesCalculation: true
        });

        const result = await freshOrder.sync(true);

        if (result.status !== Order.SYNC_STATUS_FAILED) {
            const printResult = await freshOrder.printReceipt({ choosePrinter, forceInvoiceForm });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: freshOrder };
    }

    /**
     * Pay the order. Will persist the changes
     * to the store, and sync with the server.
     */
    async pay(options = {}) {
        let {
            user,
            paymentType,
            value,
            status = Order.STATUS_PAID,
            paidOrders,
            choosePrinter,
            flag,
            openDrawer,
            forceInvoiceForm,
            registerType = null,
            registerRequest = null
        } = options;

        const validateAction = registerType !== Order.REGISTER_TYPE_EINVOICE
            ? (paymentType === 'advance' ? 'advance' : 'pay')
            : (paymentType === 'advance' ? 'einvoiceAdvance' : 'einvoice');
        const validateResult = await this.validateOrderAction(validateAction, user, { type: paymentType, value });

        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        if(registerRequest) {
            this.register_request = registerRequest;
        }

        if(flag) {
            this.setFlag(flag)
        }

        if(paymentType === 'advance') {
            status = Order.STATUS_ADVANCE;

            const refFlag = this.getFlag('ref');
            const advanceFromOldFiscalSystem = refFlag.length > 0 && refFlag[0].toLowerCase().startsWith('xxxxxxxx-xxxxxxxx');

            if(this.getToPay().toUnit() < 0 && !advanceFromOldFiscalSystem) {
                this.setFlag('type', 'closeAdvance');
            }
        }

        this.setPaid(paymentType, value);
        this.close({ user, status, paidOrders, registerType });

        const result = await this.sync();

        if (result.status !== Order.SYNC_STATUS_FAILED) {
            openDrawer = openDrawer && this.isCashExchanged();
            const printResult = await this.printReceipt({ choosePrinter, forceInvoiceForm, openDrawer });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }

            // print order
            const spilitReceiptActive = !!this.$store().getters.splitReceipt?.active
            if ((!paidOrders || !paidOrders.length) && !spilitReceiptActive) {
                setTimeout(() => this.print(), 2000);
                KitchenTicket.issue(this)
            }
        }

        this.closeReferredAdvanceOrders({ user });

        return { ...result, order: this };
    }

    /**
     * Pay for all of the specified orders using a summary order
     */
    static async payAll(paidOrders, options = {}) {
        const {
            user,
            table,
            client,
            internalNumber,
            paymentType,
            value,
            choosePrinter,
            flag,
            openDrawer,
            forceInvoiceForm,
            dueDate,
            startDate,
            endDate,
            note,
            registerType = null
        } = options;

        const status = locale === 'me' ? Order.STATUS_SUMMARY : Order.STATUS_PAID;

        const summaryOrder = Order.createNew(user, table);
        if(!paidOrders[0].paidByOrder()?.canceled()) {
            summaryOrder.hash = paidOrders[0].hash.slice(5) + paidOrders[0].hash.slice(0, 5);
        }

        summaryOrder.internal_number = internalNumber || null;
        summaryOrder.setDates(dueDate, startDate, endDate);
        summaryOrder.setNote(note);
        summaryOrder.items = locale === 'me'
            ? paidOrders.map(order => OrderItem.createForOrder(summaryOrder, order))
            : OrderItem.merge(paidOrders.flatMap(order => order.items), summaryOrder);

        paidOrders.forEach(order => {
            summaryOrder.addReference(order, 'summary');
        });

        if (client && locale === 'me') {
            summaryOrder.client_id = client.id
            summaryOrder.client = client
        } else if(client) {
            summaryOrder.setClient(client)
        }

        return await summaryOrder.pay({
            user,
            paymentType,
            value,
            status,
            paidOrders,
            choosePrinter,
            flag,
            forceInvoiceForm,
            openDrawer,
            registerType
        });
    }

    /**
     * Correct the order. Will persist the changes
     * to the store, and sync with the server.
     * Allowed only for invoice payment type.
     */
    async correct(options) {
        const { user, client = null, items = [] } = options;

        if (!items.length) return { success: false, error: i18n.t('general.action_failed') };

        const validateResult = await this.validateOrderAction('correct', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        // check the payment type
        const paymentType = CashRegister.availablePaymentTypes().find(payment => Money.parse(this[`paid_${payment.value}`]).toUnit() > 0);
        const correctiveOrder = this.createCorrectiveOrder(user, 'correct', items, paymentType?.value);

        if(client) {
            correctiveOrder.client_id = client.id;
            correctiveOrder.client = client;
        }

        correctiveOrder.close({ user, status: this.status });

        const result = await correctiveOrder.sync();

        const partiallyCorrected = correctiveOrder.getToPay().abs().toUnit() !== this.getToPay().abs().toUnit();
        const shouldPrint = locale !== 'me' || partiallyCorrected;

        if (result.status !== Order.SYNC_STATUS_FAILED && shouldPrint) {
            const openDrawer = correctiveOrder.isCashExchanged();
            const printResult = await correctiveOrder.printReceipt({ openDrawer });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: correctiveOrder };
    }

    /**
     * Cancel the order. Will persist the changes
     * to the store, and sync with the server.
     */
    async cancel(options, clearOriginalUserDebt = false) {
        if(this.isEinvoice()) {
            return this.einvoiceStorno(options);
        }

        const { user, client = null, flag } = options;

        const validateResult = await this.validateOrderAction('cancel', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        KitchenTicket.cancel(this)

        if (locale !== 'me' && this.isUnpaid()) {
            this.canceled_by_user_id = user.id;
            this.canceled_at = moment().utc().format(Order.DATE_FORMAT);
            this.sync_needed = this.$store().getters.tenant.venue.hash;

            const result = await this.sync(false);

            return { ...result, order: this };
        }

        const correctiveOrder = this.createCorrectiveOrder(user, 'cancel');

        if (clearOriginalUserDebt) {
            correctiveOrder.setFlag('clearDebtForUser', this.user_id + '')
        }

        if(client) {
            correctiveOrder.client_id = client.id;
            correctiveOrder.client = client;
        }

        if(flag) {
            correctiveOrder.setFlag(flag)
        }

        correctiveOrder.close({ user, status: this.status });

        const result = await correctiveOrder.sync(true);

        if (result.status !== Order.SYNC_STATUS_FAILED && locale !== 'me') {
            const openDrawer = correctiveOrder.isCashExchanged();
            const printResult = await correctiveOrder.printReceipt({ openDrawer });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: correctiveOrder };
    }

    /**
     * Correct the summary order by changing the payment method.
     * Will persist the changes to the store, and sync with the server.
     */
    async correctSummary(user, paymentType) {
        const validateResult = await this.validateOrderAction('correctSummary', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        const correctiveOrder = this.createCorrectiveOrder(user, 'correctSummary', [], paymentType);
        correctiveOrder.close({
            user,
            status: Order.STATUS_SUMMARY,
            paidOrders: null,
            skipTaxesCalculation: true
        });

        const result = await correctiveOrder.sync();

        return { ...result, order: correctiveOrder };
    }

    /*
     * Add credit note
     */
    static async createCreditNote(orders, items, options = {}) {
        const { user, client, internalNumber, paymentType, note, choosePrinter, flag } = options;

        const creditNoteOrder = Order.createNew(user, null);
        creditNoteOrder.status = Order.STATUS_CREDIT_NOTE;
        creditNoteOrder.client_id = client ? client.id : null;
        creditNoteOrder.internal_number = internalNumber || null;
        creditNoteOrder.note = note || null;

        creditNoteOrder.is_einvoice = orders[0].is_einvoice;

        if(locale === 'me') {
            creditNoteOrder.register_type = Order.REGISTER_TYPE_FISCAL;
        } else {
            creditNoteOrder.register_type = !EinvoiceService.einvoiceOnly()
                ? Order.REGISTER_TYPE_MANUAL
                : orders[0].register_type
        }

        creditNoteOrder.is_einvoice = creditNoteOrder.register_type === Order.REGISTER_TYPE_EINVOICE;

        // add reffered orders as order items and mark them as referred by this order
        orders.forEach(order => creditNoteOrder.credit_note_referrals.push(order.hash));

        creditNoteOrder.items = items.map(orderItem => {
            return new OrderItem({
                ...orderItem.$toJson(),
                ...{
                    id: null,
                    hash: HashGen.regular(),
                    order_id: null,
                    order_hash: creditNoteOrder.hash,
                    ref_order_hash: flag !== 'type,summaryCreditNote' ? orderItem.order_hash : null,
                    ref_order_item_hash: flag !== 'type,summaryCreditNote' ? orderItem.hash : null
                }
            });
        });

        if(flag) {
            creditNoteOrder.setFlag(flag)
        }

        creditNoteOrder.setPaid(paymentType);
        creditNoteOrder.close({ user, status: Order.STATUS_CREDIT_NOTE });

        const result = await creditNoteOrder.sync();

        if (result.status !== Order.SYNC_STATUS_FAILED) {
            const printResult = await creditNoteOrder.printReceipt({ choosePrinter });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: creditNoteOrder };
    }

    /**
     * Save the order as proforma invoice. Will persist
     * the changes to the store, and sync with the server.
     */
    async proforma(options = {}) {
        let { user, paymentType, choosePrinter, forceInvoiceForm } = options;

        const validateResult = await this.validateOrderAction('proforma', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        if (paymentType) {
            this.setPaid(paymentType);
        }

        this.close({ user, status: Order.STATUS_PROFORMA });

        const result = await this.sync();

        if (result.status !== Order.SYNC_STATUS_FAILED) {
            const printResult = await this.printReceipt({ choosePrinter, forceInvoiceForm });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: this };
    }

    /**
     * Pay a new invoice based on this proforma invoice.
     * Will persist the changes to the store, and sync with the server.
     */
    async proformaPay(options = {}) {
        const { user, choosePrinter, forceInvoiceForm } = options;

        const validateResult = await this.validateOrderAction('proformaPay', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        const freshOrder = this.fresh(user, true);
        freshOrder.addReference(this, 'proforma');

        freshOrder.close({ user, status: Order.STATUS_PAID });

        const result = await freshOrder.sync(true);

        if (result.status !== Order.SYNC_STATUS_FAILED) {
            const printResult = await freshOrder.printReceipt({ choosePrinter, forceInvoiceForm });

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: freshOrder };
    }

    /**
     * Close non-closed advance orders that this order refers to
     */
    closeReferredAdvanceOrders(options = {}) {
        const { user } = options;

        if(!this.refersToAdvance()) return;

        const advanceRefOrders = this.referredAdvanceOrders();

        // for einvocies we close reffered advance orders on BE
        if(advanceRefOrders[0].isEinvoice()) return;

        if(advanceRefOrders[0].getToPay().toUnit() < 0) return false;

        const availableAdvanceAmount = advanceRefOrders.reduce((acc, cur) => acc.add(cur.getRemainingAdvanceAmount().abs()), Money.parse(0));

        // if we have not used up all available advance and we're working with e-invoices do nothing
        const remainingAdvance = availableAdvanceAmount.subtract(this.getPaidAdvanceEffective());

        Order.closeAdvance(advanceRefOrders, {
            user: user,
            client: this.getClient(),
            orderFromAdvance: this,
            closeWith: remainingAdvance.toUnit() > 0 ? 'correct' : 'multiple',
        });
    }

    /**
     * Create a corrective receipt that cancels the specified advance orders.
     * Will persist the changes to the store, and sync with the server.
     */
    static async closeAdvance(orders, options = {}) {
        let { user, client = null, isExport = false, orderFromAdvance = null, closeWith = 'single' } = options;

        const trainingModeMismatch = orders.some(order => !!Order.store().getters.trainingMode !== !!order.isTraining())
        if(trainingModeMismatch) {
            return { status: Order.SYNC_STATUS_FAILED, error: i18n.t('general.action_failed'), order: null };
        }

        if(closeWith === 'multiple') {
            for(let i = 0; i < orders.length; i++) {
                await orders[i].cancel({ user })
            }

            return { status: Order.SYNC_STATUS_SUCCESS };
        }

        const curShift = Shift.current();

        if(!client) {
            const firstOrderWithClient = orders.find(o => !!o.client_id)
            client = firstOrderWithClient ? firstOrderWithClient.getClient() : null
        }

        const correctiveOrder = new Order({
            hash: HashGen.regular(),
            number: OrderService.getNextNumber(),
            shift_id: curShift ? curShift.id : null,
            shift_hash: curShift ? curShift.hash : null,
            user_id: user ? user.id : null,
            table_id: null,
            export: isExport,
            client_id: client?.id || null,
            client: client || null,
            flag: 'type,closeAdvance' + (closeWith === 'correct' ? ',correct' : ''),
        });

        if(closeWith === 'single') {
            correctiveOrder.items = orders.flatMap(order => order.items.map(item => item.fresh(correctiveOrder, true)));

            correctiveOrder.paid_cash = orders.reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_credit_card = orders.reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_invoice = orders.reduce((acc, cur) => acc.add(cur.getPaidInvoice()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_credit_card_business = orders.reduce((acc, cur) => acc.add(cur.getPaidCreditCardBusiness()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_advance = orders.reduce((acc, cur) => acc.add(cur.getPaidAdvance()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_check = orders.reduce((acc, cur) => acc.add(cur.getPaidCheck()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_voucher = orders.reduce((acc, cur) => acc.add(cur.getPaidVoucher()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_mobile_money = orders.reduce((acc, cur) => acc.add(cur.getPaidMobileMoney()), Money.parse(0)).multiply(-1).toFormat('0.0000');
            correctiveOrder.paid_other_noncash = orders.reduce((acc, cur) => acc.add(cur.getPaidOtherNoncash()), Money.parse(0)).multiply(-1).toFormat('0.0000');
        }

        if(closeWith === 'correct') {
            correctiveOrder.items = [
                OrderItem.createForAdvanceItem(correctiveOrder, orderFromAdvance.taxes[0], orderFromAdvance.paid_advance)
            ];

            const advanceRefOrderPaymentType = CashRegister.availablePaymentTypes().find(payment => Money.parse(orders[0][`paid_${payment.value}`]).toUnit() > 0);
            correctiveOrder.setPaid(advanceRefOrderPaymentType.value);
        }

        orders.sort((o1, o2) => new Date(o2.last_activity_at) - new Date(o1.last_activity_at))
            .forEach(order => correctiveOrder.addReference(order, 'corrective'))

        const validateResult = await correctiveOrder.validateOrderAction('closeAdvance', user);
        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: correctiveOrder };
        }

        correctiveOrder.close({ user, status: Order.STATUS_ADVANCE });

        const result = await correctiveOrder.sync(true);

        if (result.status !== Order.SYNC_STATUS_FAILED && locale === 'sr') {
            const printResult = await correctiveOrder.printReceipt();

            if (!printResult.success) {
                result.printError = printResult.errorCode
                    ? i18n.t(`print.${printResult.errorCode}`)
                    : printResult.error;
                result.status = Order.SYNC_STATUS_SUCCESS_WITH_ERRORS;
            }
        }

        return { ...result, order: correctiveOrder };
    }

    /**
     * Create an E-Invoice for this receipt and cancel it afterwards.
     */
    async einvoiceFromFiscal(options = {}) {
        let { user, registerRequest, dueDate } = options;

        if (!this.$store().getters.isOnline) {
            return {
                status: Order.SYNC_STATUS_FAILED,
                error: i18n.t('connection.internet_offline_message_not_allowed')
            };
        }

        const sdcStatus = await FiscalService.checkSdcStatus();
        if (sdcStatus.error) {
            return {
                status: Order.SYNC_STATUS_FAILED,
                error: sdcStatus.error.message
            };
        }

        const freshOrder = this.fresh(user);
        freshOrder.addReference(this, 'einvoice');

        if(dueDate) {
            freshOrder.setDates(dueDate);
        }

        const result = await freshOrder.pay({
            user,
            status: this.status,
            paymentType: 'invoice',
            forceInvoiceForm: true,
            registerType: Order.REGISTER_TYPE_EINVOICE,
            registerRequest,
        });

        if(result.status === Order.SYNC_STATUS_SUCCESS) {
            const cancelResult = await this.cancel({ user });

            if(cancelResult.status === Order.SYNC_STATUS_FAILED) {
                return {
                    status: Order.SYNC_STATUS_SUCCESS_WITH_ERRORS,
                    error: i18n.t('einvoice.fiscal_cancel_needed')
                };
            }
        }

        return result;
    }

    /**
     * Add reference to the other order either by making
     * this order refer to the added one or vice versa.
     */
    addReference(order, referenceType, referredByOther = false) {
        const refOrder = order.$duplicate();
        delete refOrder.reference;

        refOrder.reference = {
            referred_to_order_hash: !referredByOther ? refOrder.hash : this.hash,
            referred_by_order_hash: !referredByOther ? this.hash : refOrder.hash,
            type: referenceType
        };

        if (!referredByOther) {
            this.refers_to.push(refOrder);
        } else {
            this.referred_by.push(refOrder);
        }
    }

    /**
     * Give order discount. Will persist the changes to the store, and sync
     * with the server if the order is not new / has previously been issued.
     */
    async giveDiscount(percent, sync = false) {
        if (percent < 0 || percent > 99 || this.isSummary()) return { success: false, order: this };

        const uniqueDiscounts = this.items
            .map(item => item.getDiscountPercent())
            .filter((value, index, array) => index === array.indexOf(value));

        if (uniqueDiscounts.length > 1 || uniqueDiscounts[0] == percent) return { success: false, order: this };

        this.items.forEach(orderItem => orderItem.giveDiscount(percent));

        this.taxes = Order.calculateTaxes(this);

        if (sync && !this.isNew()) {
            this.sync_needed = this.$store().getters.tenant.venue.hash;
            const syncResult = await this.sync(false);
            return { success: true, order: this, syncResult };
        }

        return { success: true, order: this };
    }

    /**
     * Update item count
     */
    async updateItemCount(item, count, sync = false) {
        if(count > 999999 || this.isSummary()) return { success: false, order: this };

        if (count == 0) {
            return this.deleteItem(item, sync);
        }

        const orderItem = this.items.find(orderItem => orderItem.hash === item.hash);
        if (orderItem) {
            orderItem.updateCount(count);
        }

        if (orderItem.getToPay().toFormat('0.00') === '0.00') {
            return this.deleteItem(item, sync);
        }

        this.taxes = Order.calculateTaxes(this);

        if (sync && !this.isNew()) {
            this.sync_needed = this.$store().getters.tenant.venue.hash;
            const syncResult = await this.sync(false);
            return { success: true, order: this, syncResult };
        }

        return { success: true, order: this };
    }

    /**
     * Update item unit price
     */
    updateItemUnitPrice(item, price) {
        const limit = locale === 'sr' ? 29999999 : 99999;
        if (price <= 0 || price > limit) return;

        if(this.isSummary()) return;

        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.updateUnitPrice(price);
            }
        });

        this.taxes = Order.calculateTaxes(this);
    }

    /**
     * Give item discount
     */
    async giveItemDiscount(item, percent, sync = false) {
        if (percent < 0 || percent > 99 || this.isSummary()) return { success: false, order: this };

        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.giveDiscount(percent);
            }
        });

        this.taxes = Order.calculateTaxes(this);

        if (sync && !this.isNew()) {
            this.sync_needed = this.$store().getters.tenant.venue.hash;
            const syncResult = await this.sync(false);
            return { success: true, order: this, syncResult };
        }

        return { success: true, order: this };
    }

    /**
     * Set the pricelist for the order
     */
    async setPricelist(pricelist = 0, sync = false) {
        if(this.isSummary()) return { success: false, order: this };

        const existingPricelist = this.getPricelist();
        if(existingPricelist === pricelist) return { success: false, order: this };

        if(pricelist) {
            this.setFlag('pricelist', pricelist);
        } else {
            this.removeFlag('pricelist');
        }

        this.items.forEach(orderItem => orderItem.setPricelist(this, pricelist));

        this.taxes = Order.calculateTaxes(this);

        if (sync && !this.isNew()) {
            this.sync_needed = this.$store().getters.tenant.venue.hash;
            const syncResult = await this.sync(false);
            return { success: true, order: this, syncResult };
        }

        return { success: true, order: this };
    }

    /**
     * Get the pricelist of the order
     */
    getPricelist() {
        const pricelist = this.getFlag('pricelist');
        if(!pricelist.length) return 0;

        return parseInt(pricelist[0]);
    }

    /**
     * Add order item. If the order already contains new / not yet issued item
     * for the same product, update it. Otherwise create a new item.
     */
    addItem(product, count = null) {
        if(count && count > 999999) return null;
        if(product.getSellingPrice().toUnit() < 0) return null;

        if(this.isSummary()) return;

        const forceSeparate = !!this.$store().getters.tenant.venue?.settings?.item_force_separate;

        const separators = this.getItemSeparators();
        let matchedItems = this.items.filter((item, index) => {
            const isInLastSection = separators.length === 0 || separators[separators.length - 1] <= index
            return isInLastSection && item.product_id === product.id && item.getDiscountAmount().toUnit() == 0;
        });

        let itemToUpdate = !forceSeparate && matchedItems.length > 0 ? matchedItems[matchedItems.length - 1] : undefined

        let countToAdd = count || (!product.is_made ? parseFloat(product.selling_count) : 1);

        if (countToAdd <= 0) return null;

        if (itemToUpdate) {
            // move item to the last position in the array
            this.items.push(this.items.splice(this.items.indexOf(itemToUpdate), 1)[0]);
            itemToUpdate.updateCount(parseFloat(itemToUpdate.count) + countToAdd);
            this.taxes = Order.calculateTaxes(this);
            return itemToUpdate;
        }

        const newOrderItem = OrderItem.createForProduct(this, product, countToAdd);

        this.items.push(newOrderItem);
        this.taxes = Order.calculateTaxes(this);

        return newOrderItem;
    }

    /**
     * Delete OrderItem
     */
    async deleteItem(item, sync = false) {
        if(this.isSummary()) return { success: false, order: this };

        this.items = this.items.filter(orderItem => orderItem.hash !== item.hash);
        this.taxes = Order.calculateTaxes(this);

        const isEmpty = !this.items.length;

        let syncResult = null;
        if (sync && !this.isNew()) {
            KitchenTicket.deleteItem(this, item)

            this.sync_needed = this.$store().getters.tenant.venue.hash;
            syncResult = await this.sync(false);
        }

        if(isEmpty) {
            if(!this.isNew()) AppEvent.build('orderDelete', { id: this.id }).send()
            this.$delete()
        }

        const result = { success: true, order: !isEmpty ? this : null }
        if(syncResult) {
            result.syncResult = syncResult;
        }

        return result;
    }

    /**
     * Exempt item from tax
     */
    setItemExemptFromTax(item, exemptFromTaxReason) {
        if(this.isSummary()) return;

        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.setExemptFromTax(exemptFromTaxReason);
            }
        });

        this.taxes = Order.calculateTaxes(this);
    }

    /**
     * Assign the Client to the order.
     */
    async setClient(client, applyDiscount = true, sync = false) {
        const existingClient = this.client || this.getClient();
        const existingZeroTaxMode = this.export || existingClient?.isDiplomat();
        const zeroTaxMode = this.export || client?.isDiplomat();

        if((!client && !this.client_id) || client?.id === this.client_id || this.isSummary()) {
            return { success: false, order: this }
        }

        this.client_id = client ? client.id : null;
        this.client = client;

        const curPricelistId = this.getPricelist();
        const existingClientPricelistId = existingClient?.pricelist_id;
        const clientPricelistId = client?.pricelist_id;

        if(clientPricelistId && clientPricelistId !== curPricelistId) {
            this.setPricelist(client.pricelist_id);
        }

        if(!client && existingClientPricelistId && existingClientPricelistId === curPricelistId) {
            this.setPricelist(0);
        }

        if (applyDiscount && client?.getDiscountPercent()) {
            this.giveDiscount(client.getDiscountPercent());
        }

        if(zeroTaxMode) {
            this.setZeroItemsTax(client?.isDiplomat() ? 'diplomat' : 'export');
        } else if(existingZeroTaxMode) {
            this.setOriginalItemsTax();
        }

        if (sync && !this.isNew()) {
            this.sync_needed = this.$store().getters.tenant.venue.hash;
            const syncResult = await this.sync(false);
            return { success: true, order: this, syncResult };
        }
    }

    /**
     * Set Item Comments
     */
    setItemComments(item, comments) {
        if(this.isSummary()) return;

        this.items.forEach(orderItem => {
            if (orderItem.hash === item.hash) {
                orderItem.setComments(comments);
            }
        });
    }

    /**
     * Set Note
     */
    setNote(note = null) {
        this.note = note || null;
    }

    /**
     * Set Dates
     */
    setDates(dueDate = null, startDate = null, endDate = null) {
        this.due_date = dueDate || null;
        this.start_date = startDate || null;
        this.end_date = endDate || null;
    }

    /**
     * Set Tax Period
     */
    setTaxPeriod(taxPeriod = null) {
        this.tax_period = taxPeriod || null;
    }

    /**
     * Get IsIncoming
     */
    isIncoming() {
        return !!this.incoming;
    }

    /**
     * Get IsEinvoice
     */
    isEinvoice() {
        return this.register_type === Order.REGISTER_TYPE_EINVOICE || this.is_einvoice;
    }

    /**
     * Check if order is fiscal
     */
    isFiscal() {
        return this.register_type === Order.REGISTER_TYPE_FISCAL || (!this.register_type && !this.is_einvoice && !EinvoiceService.einvoiceOnly());
    }

    /**
     * Get IsExternal
     */
    isExternal() {
        return this.register_type === Order.REGISTER_TYPE_EXTERNAL
    }

    /**
     * Set register type
     */
    setRegisterType(registerType = null) {
        if(registerType) {
            this.register_type = registerType;
            this.is_einvoice = this.register_type === Order.REGISTER_TYPE_EINVOICE;
            return;
        }

        // if already set, return
        if(this.register_type) return;

        // if not already set, determine it if possible
        const isFiscalStatus = Order.fiscalStatuses().includes(this.status);
        if(locale === 'me') {
            if(isFiscalStatus) {
                this.register_type = Order.REGISTER_TYPE_FISCAL;
            }
        } else {
            if(!EinvoiceService.einvoiceOnly()) {
                if(isFiscalStatus){
                    this.register_type = Order.REGISTER_TYPE_FISCAL;
                } else if(this.isCreditNote()){
                    this.register_type = Order.REGISTER_TYPE_MANUAL;
                }
            }
        }

        this.is_einvoice = this.register_type === Order.REGISTER_TYPE_EINVOICE;
    }

    /**
     * Check if register status of E-Invoice is failed
     */
    einvoiceIsFailed() {
        return this.isEinvoice() && this.register_status === 'failed';
    }

    /**
     * Check if register status of E-Invoice is sending
     */
    einvoiceIsSending() {
        return this.isEinvoice() && this.register_status === 'einvoice-sending';
    }

    /**
     * Check if register status of E-Invoice is sent
     */
    einvoiceIsSent() {
        return this.isEinvoice() && this.register_status === 'einvoice-sent';
    }

    /**
     * Check if register status of E-Invoice is rejected
     */
    einvoiceIsRejected() {
        return this.isEinvoice() && this.register_status === 'einvoice-rejected';
    }

    /**
     * Check if register status of E-Invoice is approved
     */
    einvoiceIsApproved() {
        return this.isEinvoice() && this.register_status === 'einvoice-approved';
    }

    /**
     * Check if register status of E-Invoice is cancelled
     */
    einvoiceIsCancelled() {
        return this.isEinvoice() && this.register_status === 'einvoice-cancelled';
    }

    /**
     * Check if register status of E-Invoice is storno
     */
    einvoiceIsStorno() {
        return this.isEinvoice() && this.register_status === 'einvoice-storno';
    }

    /**
     * Check if register status of E-Invoice is seen
     */
    einvoiceIsSeen() {
        return this.isEinvoice() && this.register_status === 'einvoice-seen';
    }

    /**
     * Check if register status of E-Invoice is new
     */
    einvoiceIsNew() {
        return this.isEinvoice() && this.register_status === 'einvoice-new';
    }

    /**
     * Approve Einvoice
     */
    async einvoiceApprove(options) {
        const { user, comment = null } = options;

        const validateResult = await this.validateOrderAction('einvoiceApprove', user);

        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        const result = await this.einvoiceSend('approve', comment);

        return { ...result, order: this };
    }

    /**
     * Reject Einvoice
     */
    async einvoiceReject(options) {
        const { user, comment = null } = options;

        const validateResult = await this.validateOrderAction('einvoiceReject', user);

        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        const result = await this.einvoiceSend('reject', comment);

        return { ...result, order: this };
    }

    /**
     * Storno Einvoice
     */
    async einvoiceStorno(options) {
        const { user, comment } = options;

        const validateResult = await this.validateOrderAction('einvoiceStorno', user);

        if (!validateResult.success) {
            return { status: Order.SYNC_STATUS_FAILED, error: validateResult.error, order: this };
        }

        const result = await this.einvoiceSend('storno', comment);

        return { ...result, order: this };
    }

    /**
     * Refresh Einvoice
     */
    async einvoiceRefresh() {
        if (!this.isEinvoice()) return { success: false };

        if (!this.$store().getters.isOnline) return { success: false };

        try {
            const response = await ApiService.patch(`${isAllegra ? '/allegra' : ''}/orders/einvoice/${this.id}/refresh-sef-status`);

            if(!response.data.success) return { success: false };

            if (response.data.order) {
                this.$fill(response.data.order);
                Order.insert({ data: [response.data.order], freeze: true });
            }

            return { success: true, order: response.data.order };
        } catch(error) {
            return { success: false, error: error.message };
        }
    }

    /**
     * Send E-Invoice
     */
    async einvoiceSend(statusUpdate = null, comment = null) {
        try {
            const urlPrefix = isAllegra ? '/allegra' : '';
            const urlSuffix = statusUpdate ? `/${this.id}/${statusUpdate}` : '';
            const url = `${urlPrefix}/orders/einvoice${urlSuffix}`;
            const method = statusUpdate ? 'patch' : 'post';

            if(statusUpdate) {
                this.register_request = this.register_request
                    ? { ...this.register_request, comment }
                    : { comment }
            }

            const response = await ApiService[method](url, { order: this.$toJson() });

            if (response.data.order) {
                if (response.data.order.hash === this.hash) {
                    this.$fill(response.data.order);
                }

                if (this.number) {
                    OrderService.setLatestNumber(this.number, true);
                }

                Order.insert({ data: [response.data.order], freeze: true });
            }

            if (response.data.ref_orders && response.data.ref_orders.length) {
                Order.insert({ data: response.data.ref_orders, freeze: true });
            }

            if (response.data.client) {
                Client.update(response.data.client);
            }

            if (response.data.product_counts && response.data.product_counts.length) {
                Product.update(response.data.product_counts);
            }

            if (response.data.error) {
                return { status: Order.SYNC_STATUS_FAILED, error: i18n.t(`order.error.${response.data.error}`) };
            }

            return { status: Order.SYNC_STATUS_SUCCESS };
        } catch (error) {
            console.log(error.message)
            return { status: Order.SYNC_STATUS_FAILED, error: i18n.t('order.error.order_issue_failed') }
        }
    }

    /**
     * Set Export
     */
    setExport(isExport = false) {
        if(this.isSummary()) return;

        const existingClient = this.client || this.getClient();
        const existingZeroTaxMode = this.export || existingClient?.isDiplomat();
        const zeroTaxMode = isExport || existingClient?.isDiplomat();

        this.export = isExport;

        if(zeroTaxMode) {
            this.setZeroItemsTax(isExport ? 'export' : 'diplomat');
        } else if(existingZeroTaxMode) {
            this.setOriginalItemsTax();
        }
    }

    /**
     * Set Zero Tax
     */
    setZeroItemsTax(reason) {
        if(this.isSummary()) return;

        this.items = this.items.map(item => {
            item.setZeroTax(reason);
            return item;
        });
        this.taxes = Order.calculateTaxes(this);
    }

    /**
     * Set Original Items Tax
     */
    setOriginalItemsTax() {
        if(this.isSummary()) return;

        this.items = this.items.map(item => {
            item.setOriginalItemsTax();
            return item;
        });
        this.taxes = Order.calculateTaxes(this);
    }

    /**
     * Set Custom Reference
     */
    setCustomReference(refOrder, refTime) {
        if(!refOrder || !refTime) {
            this.removeFlag('ref')
            return;
        }

        this.setFlag('ref', refOrder, refTime)
    }

    /**
     * Set item separator
     */
    setItemSeparator() {
        const separatorIndex = '' + this.items.length;

        if(!this.flagsInclude('separator')) {
            this.setFlag('separator', separatorIndex);
            return;
        }

        let segments = this.getFlag('separator');

        if(segments.includes(separatorIndex)) {
            segments = segments.filter(segment => segment !== separatorIndex);
        } else {
            segments.push(separatorIndex);
        }

        if(!segments.length) {
            this.removeFlag('separator');
            return;
        }

        this.setFlag('separator', ...segments.sort((i1, i2) => i1 - i2));
    }

    /**
     * Get item separators
     */
    getItemSeparators() {
        return this.getFlag('separator');
    }

    /**
     * Add flag to the order
     */
    setFlag(value, ...args) {
        if(!args.length) {
            this.flag = this.flag ? `${this.flag}|${value}` : value;
            return;
        }

        const prefix = value;

        const flags = this.flag ? this.flag.split('|') : [];
        const flagIndex = flags.findIndex(flag => flag.startsWith(prefix));

        if(flagIndex !== -1) {
            flags[flagIndex] = `${prefix},${args.join(',')}`
        } else {
            flags.push(`${prefix},${args.join(',')}`)
        }

        this.flag = flags.join('|');
    }

    /**
     * Get flag values
     */
    getFlag(prefix) {
        const flags = this.flag ? this.flag.split('|') : [];
        const flag = flags.find(flag => flag.startsWith(prefix + ','));

        if(!flag) return [];

        return flag.split(',').slice(1);
    }

    /**
     * Remove flag
     */
    removeFlag(prefix) {
        if(!prefix) {
            this.flag = null;
            return;
        }

        let flags = this.flag ? this.flag.split('|') : [];
        flags = flags.filter(flag => !flag.startsWith(prefix + ','));
        if(!flags.length) {
            this.flag = null;
            return;
        }

        this.flag = flags.join('|');
    }

    /**
     * Check if the flags include a specific flag
     */
    flagsInclude(value, prefix = null) {
        if(!this.flag) return false;

        if(!prefix) return this.flag.includes(value);

        const flags = this.flag.split('|');
        const flag = flags.find(flag => flag.startsWith(prefix + ','));

        if(!flag) return false;

        return flag.split(',').slice(1).some(val => val === value)
    }

    /**
     * Set the guest id on an order
     */
    setGuestId(guest) {
        this.setFlag('guest', guest)
    }

    /**
     * Get the guest id of the order
     */
    getGuestId() {
        const guestFlag = this.getFlag('guest');
        if(!guestFlag.length) return null;

        return parseInt(guestFlag[0]);
    }

    /**
     * Correct items of the regular orders with items
     * from the corrective orders in the array
     */
    static correctOrders(orders) {
        const correctiveOrders = orders.filter(order => order.correctsOthers());

        if (correctiveOrders.length === 0) {
            return orders;
        }

        const nonCorrectiveOrders = orders.filter(order => !order.correctsOthers());

        return nonCorrectiveOrders.map(order => {
            const correctedByOrder = correctiveOrders.find(o => {
                const correctsOrder = o.correctsOrders()[0];
                return correctsOrder && correctsOrder.hash === order.hash;
            });

            if (correctedByOrder) {
                order.items = order.items
                    .map(item => {
                        const correctedByItem = correctedByOrder.items.find(
                            i => i.product_id === item.product_id && i.discount_percent === item.discount_percent
                        );

                        item.count = correctedByItem
                            ? (item.getCount() + correctedByItem.getCount()).toFixed(3)
                            : item.count;
                        return item;
                    })
                    .filter(item => item.count > 0);
            }
            return order;
        });
    }

    getTaxes() {
        if (locale === 'sr' && !this.isCreditNote() && this.isFiscal()) {
            return this.getTaxesSrFiscal();
        }

        return [
            ...this.getTaxesOnItems(),
            ...this.getTaxesOnTotal()
        ]
    }

    getTaxesOnItems() {
        const taxItems = this.items.map(item => TaxItem.createForOrderItem(this, item))
            .filter(taxItem => !!taxItem);

        return TaxItem.merge(taxItems);
    }

    getTaxesOnTotal() {
        const taxItems = TaxRate.all()
            .filter(taxRate => taxRate.type === TaxRate.TYPE_ORDER)
            .map(taxRate => TaxItem.createForTaxRateOnTotal(this, taxRate));

        return TaxItem.merge(taxItems);
    }

    getTaxesSrFiscal() {
        const invoice = this.register_request || this.getRegisterRequestSr();
        const taxesCalculation = FiscalService.calculateTaxes(invoice);
        const taxItems = taxesCalculation ? taxesCalculation.taxItems : [];

        return taxItems.map(taxItem => TaxItem.createForSrFiscalTaxItem(this, taxItem, invoice));
    }

    /**
     * Calculate Taxes
     */
    static calculateTaxes(orders) {
        if (!Array.isArray(orders)) {
            orders = [orders];
        }

        if(orders.length === 1 && orders[0].isSummary()) {
            orders = orders[0].paysForOrders();
        }

        orders = orders.map(order => order.$duplicate());

        // if there are multiple orders correct regular ones with corrective ones
        if (orders.length > 1) {
            orders = Order.correctOrders(orders);
        }

        const taxItems = orders.filter(order => !order.isSummary())
            .flatMap(order => order.getTaxes());

        return TaxItem.merge(taxItems);
    }

    /**
     * Generate QR Code string
     */
    generateQRCodeString() {
        if (this.register_response?.verificationUrl) {
            return this.register_response.verificationUrl;
        }

        if (locale !== 'me') return '';

        const tenant = this.$store().getters.tenant;
        const appConfig = this.$store().getters.appConfig;
        let cashRegister = CashRegister.find(this.cash_register_id);

        if (!tenant) {
            return null;
        }

        const baseUrl = appConfig.fiscal.efiQrCodeUrl;
        const iic = this.getIic();
        const tin = tenant.venue.tax_id_number;
        const crtd = moment.utc(this.last_activity_at).local().format();
        const ord = this.number;
        const bu = tenant.venue.hash;
        const cr = cashRegister.hash;
        const sw = appConfig.fiscal.softwareCode;
        const prc = this.getToPay().toFormat('0.00');

        return `${baseUrl}?iic=${iic}&tin=${tin}&crtd=${crtd}&ord=${ord}&bu=${bu}&cr=${cr}&sw=${sw}&prc=${prc}`;
    }

    /**
     * Print receipt
     */
    printReceipt(options = {}) {
        return PrintService.printReceipt(this, options);
    }

    /**
     * Print order
     */
    print() {
        let items = {};
        Product.PRINTING_SECTORS.forEach(printingSector => {
            items[printingSector] = [];
        });

        this.items.forEach(item => {
            const product = item.getProduct();
            if (product.printing_sector) {
                items[product.printing_sector].push(item);
            }
        });

        // print order
        const orderPrinter = PrintService.getAssignedPrinter('order');
        if (orderPrinter) {
            PrintService.printOrder(this, orderPrinter, 'order');
        }

        // print in printing sectors
        let printerIndex = 1;
        Product.PRINTING_SECTORS.forEach(printingSector => {
            let assignedPrinter = PrintService.getAssignedPrinter(printingSector);

            if (items[printingSector].length && assignedPrinter) {
                const delay = printerIndex * 2000;

                setTimeout(() => {
                    PrintService.printOrder(this, assignedPrinter, printingSector);
                }, delay);

                printerIndex++;
            }
        });
    }

    /**
     * Get The Order Payment Type
     */
    paymentType() {
        if (this.isUnpaid()) return i18n.t('orders.payment_types.order');
        if (this.isProforma()) return i18n.t('orders.payment_types.proforma');

        let type = null;

        if (!this.getPaidCash().isZero()) {
            type = i18n.t('orders.payment_types.cash');
        }

        if (!this.getPaidCreditCard().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.credit_card');
        }

        if (!this.getPaidInvoice().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.invoice');
        }

        if (!this.getPaidCreditCardBusiness().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.credit_card_business');
        }

        if (!this.getPaidAdvance().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.advance');
        }

        if (!this.getPaidCheck().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.check');
        }

        if (!this.getPaidVoucher().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.voucher');
        }

        if (!this.getPaidMobileMoney().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.mobile_money');
        }

        if (!this.getPaidOtherNoncash().isZero()) {
            type = (type ? type + ', ' : '') + i18n.t('orders.payment_types.other_noncash');
        }

        return type;
    }

    isNew() {
        return this.status === Order.STATUS_NEW;
    }

    isUnpaid() {
        return this.status === Order.STATUS_UNPAID;
    }

    isPaid() {
        return this.status === Order.STATUS_PAID;
    }

    isSummary() {
        return this.status === Order.STATUS_SUMMARY;
    }

    isCreditNote() {
        return this.status === Order.STATUS_CREDIT_NOTE;
    }

    isProforma() {
        return this.status === Order.STATUS_PROFORMA;
    }

    isTraining() {
        return this.status === Order.STATUS_TRAINING;
    }

    isAdvance() {
        return this.status === Order.STATUS_ADVANCE;
    }

    refersToAdvance() {
        return this.refers_to.some(order => order.reference?.type === 'advance');
    }

    isCopy() {
        return this.status === Order.STATUS_COPY;
    }

    isNonCash() {
        if(this.getToPay().toUnit() == 0) return true;

        return this.getPaidCash().toUnit() == 0 && this.getPaidCreditCard().toUnit() == 0
    }

    correctsOthers() {
        return this.refers_to.some(order => order.reference?.type === 'corrective');
    }

    corrected() {
        return this.referred_by.some(order => order.reference?.type === 'corrective');
    }

    canceled() {
        return !!this.canceled_at;
    }

    unpaidNoSummary() {
        return (
            this.isUnpaid() && !this.referred_by.some(order => order.reference?.type === 'summary' && !order.canceled())
        );
    }

    unpaidWithSummary() {
        return this.isUnpaid() && this.referred_by.some(order => order.reference?.type === 'summary' && !order.canceled());
    }

    advanceClosed() {
        const ordersFromAdvanceTotal = this.ordersFromAdvance().reduce((acc, cur) => acc.add(cur.getPaidAdvance().abs()), Money.parse(0));
        return ordersFromAdvanceTotal.toUnit() >= this.getToPay().abs().toUnit();
    }

    ordersFromAdvance() {
        return this.referred_by.filter(order => order.reference?.type === 'advance');
    }

    referredAdvanceOrders() {
        return this.refers_to.filter(order => order.reference?.type === 'advance');
    }

    ordersFromProforma() {
        return this.referred_by.filter(order => order.reference?.type === 'proforma');
    }

    isSale() {
        if(locale === 'me') {
            return this.isPaid() || this.isUnpaid() || this.isAdvance() || this.isCreditNote();
        } else {
            return this.isPaid() || this.isAdvance();
        }
    }

    isMoneyExchanged() {
        return this.isPaid() || (this.isSummary() && !this.canceled()) || (this.isAdvance() && !this.flagsInclude('closeAdvance'));
    }

    isCashExchanged() {
        const skipDebt = this.getFlag('debt').includes('skip');
        return !skipDebt && this.isMoneyExchanged() && (this.getPaidCash().toUnit() !== 0 || this.getChange().toUnit() !== 0);
    }

    isMixedPayment() {
        let paymentTypesCount = 0;

        if(this.getPaidCash().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidCreditCard().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidInvoice().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidCreditCardBusiness().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidAdvance().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidCheck().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidVoucher().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidMobileMoney().toUnit() !== 0) paymentTypesCount++;
        if(this.getPaidOtherNoncash().toUnit() !== 0) paymentTypesCount++;

        return paymentTypesCount > 1;
    }

    getPaidCash() {
        return Money.parse(this.paid_cash);
    }

    getPaidCreditCard() {
        return Money.parse(this.paid_credit_card);
    }

    getPaidInvoice() {
        return Money.parse(this.paid_invoice);
    }

    getPaidCreditCardBusiness() {
        return Money.parse(this.paid_credit_card_business);
    }

    getPaidAdvance() {
        return Money.parse(this.paid_advance);
    }

    getPaidCheck() {
        return Money.parse(this.paid_check);
    }

    getPaidVoucher() {
        return Money.parse(this.paid_voucher);
    }

    getPaidMobileMoney() {
        return Money.parse(this.paid_mobile_money);
    }

    getPaidOtherNoncash() {
        return Money.parse(this.paid_other_noncash);
    }

    getChange() {
        return Money.parse(this.change);
    }

    getTip() {
        return Money.parse(this.tip);
    }

    getCashSale() {
        return this.getPaidCash().add(this.getPaidCreditCard());
    }

    getNonCashSale() {
        return this.getPaidInvoice()
            .add(this.getPaidCreditCardBusiness())
            .add(this.getPaidAdvance())
            .add(this.getPaidCheck())
            .add(this.getPaidVoucher())
            .add(this.getPaidMobileMoney())
            .add(this.getPaidOtherNoncash())
    }

    getLastActivityAt() {
        if (!this.last_activity_at) {
            return null;
        }

        return moment.utc(this.last_activity_at).local();
    }

    /**
     * Lazy Get User
     */
    getUser() {
        return User.find(this.user_id);
    }

    /**
     * Lazy Get Client
     */
    getClient() {
        return Client.find(this.client_id);
    }

    /**
     * Lazy Get Table
     */
    getTable() {
        return Table.find(this.table_id);
    }

    /**
     * Lazy Get Sector
     */
    getSector() {
        const table = this.getTable()
        if(table) return Sector.find(table.sector_id)
        return undefined
    }

    // Additional getters
    getPrice() {
        if (this.isSummary()) {
            return this.paysForOrders().reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0));
        }

        if (this.isCreditNote()) {
            return this.getToPay();
        }

        return this.items.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0));
    }

    getToPay() {
        if (this.isSummary()) {
            return this.paysForOrders().reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
        }

        if (this.isCreditNote()) {
            return this.items.reduce((acc, cur) => acc.add(cur.getAdditionalToPay()), Money.parse(0));
        }

        return this.items.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
    }

    getLeftToPay() {
        return this.getToPay().subtract(this.getPaidAdvanceEffective());
    }

    getPriceBeforeTax() {
        if (locale === 'sr') {
            if(this.register_response && this.isFiscal()) {
                const totalTax = this.register_response.taxItems.reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.amount).toRoundedUnit(2))), Money.parse(0));
                return Money.parse(this.register_response.totalAmount).subtract(totalTax);
            }

            return this.getToPay().abs().subtract(this.getTax());
        }

        if (this.isSummary()) {
            return this.paysForOrders().reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0));
        }

        if (this.isCreditNote()) {
            return this.items.reduce((acc, cur) => acc.add(cur.getAdditionalPriceBeforeTax()), Money.parse(0));
        }

        return this.items.reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0));
    }

    getExemptFromTax() {
        if (this.isSummary()) {
            return this.paysForOrders().reduce((acc, cur) => acc.add(cur.getExemptFromTax()), Money.parse(0));
        }

        if (this.isCreditNote()) {
            return this.items
                .filter(item => !!item.exempt_from_tax)
                .reduce((acc, cur) => acc.add(cur.getAdditionalToPay()), Money.parse(0));
        }

        return this.items
            .filter(item => !!item.exempt_from_tax)
            .reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
    }

    getTax() {
        if (locale === 'sr') {
            if(this.register_response && this.isFiscal()) {
                return this.register_response.taxItems.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0));
            }

            return this.taxes.reduce((acc, cur) => {
                const taxRateAmount = Money.parse(cur.value);
                return acc.add(Money.parse(taxRateAmount.toRoundedUnit(2)));
            }, Money.parse(0));
        }

        if (this.isSummary()) {
            return this.paysForOrders().reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
        }

        if (this.isCreditNote()) {
            return this.items
                .filter(item => !item.exempt_from_tax)
                .reduce((acc, cur) => acc.add(cur.getAdditionalTax()), Money.parse(0));
        }

        return this.items
            .filter(item => !item.exempt_from_tax)
            .reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
    }

    getDiscountAmount() {
        if (this.isSummary()) {
            return this.paysForOrders().reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0));
        }

        if (this.isCreditNote()) {
            return Money.parse(0);
        }

        return this.items.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0));
    }

    getAdditionalDiscountAmount() {
        return this.items
            .filter(item => item.getAdditionalDiscountPercent() != 0)
            .reduce((acc, cur) => acc.add(cur.getAdditionalPriceBeforeTax()), Money.parse(0));
    }

    getAdditionalCountAmount() {
        return this.items
            .filter(item => item.getAdditionalCount() != 0)
            .reduce((acc, cur) => acc.add(cur.getAdditionalPriceBeforeTax()), Money.parse(0));
    }

    getPaid() {
        if(locale === 'sr' && this.register_request && this.isFiscal()) {
            const totalPaid = this.register_request.payment.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0));
            const advancePaid = this.register_response?.advancePaid
                ? Money.parse(this.register_response.advancePaid)
                : this.getPaidAdvance()

            return totalPaid.add(advancePaid);
        }

        return this.getPaidCash()
            .add(this.getPaidCreditCard())
            .add(this.getPaidInvoice())
            .add(this.getPaidCreditCardBusiness())
            .add(this.getPaidAdvance())
            .add(this.getPaidCheck())
            .add(this.getPaidVoucher())
            .add(this.getPaidMobileMoney())
            .add(this.getPaidOtherNoncash());
    }

    getPayments() {
        return [
            { value: 'advance', amount: this.getPaidAdvance(), display: i18n.t('receipt.payments.advance') },
            { value: 'cash', amount: this.getPaidCash(), display: i18n.t('receipt.payments.cash') },
            { value: 'credit_card', amount: this.getPaidCreditCard(), display: i18n.t('receipt.payments.credit_card') },
            { value: 'invoice', amount: this.getPaidInvoice(), display: i18n.t('receipt.payments.invoice') },
            { value: 'credit_card_business', amount: this.getPaidCreditCardBusiness(), display: i18n.t('receipt.payments.credit_card_business') },
            { value: 'check', amount: this.getPaidCheck(), display: i18n.t('receipt.payments.check') },
            { value: 'voucher', amount: this.getPaidVoucher(), display: i18n.t('receipt.payments.voucher') },
            { value: 'mobile_money', amount: this.getPaidMobileMoney(), display: i18n.t('receipt.payments.mobile_money') },
            { value: 'other_noncash', amount: this.getPaidOtherNoncash(), display: i18n.t('receipt.payments.other_noncash') },
        ].filter(p => p.amount.toUnit() !== 0)
    }

    getPaidAdvanceEffective() {
        if(this.getPaidAdvance().toUnit() == 0) return Money.parse(0);
        if(this.getPaidAdvance().toUnit() > 0 && this.getPaidAdvance().toUnit() > this.getToPay().toUnit()) return this.getToPay();
        if(this.getPaidAdvance().toUnit() < 0 && this.getPaidAdvance().toUnit() < this.getToPay().toUnit()) return this.getToPay();

        return this.getPaidAdvance();
    }

    getCorrectedAmount() {
        return this.correctedByOrders().reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0));
    }

    getRemainingAdvanceAmount() {
        return this.getToPay().add(this.getCorrectedAmount());
    }

    getRemainingTaxes() {
        const correctedByOrdersTaxes = this.correctedByOrders().flatMap(o => o.taxes);
        return TaxItem.merge([...correctedByOrdersTaxes, ...this.taxes]);
    }

    /**
     * Get the IIC Signature
     */
    getIicSignature() {
        let tenant = this.$store().getters.tenant;

        if (!tenant) {
            return null;
        }

        let cashRegister = CashRegister.find(this.cash_register_id);

        let iic = '';

        // issuerTIN
        iic += tenant.venue.tax_id_number;
        // dateTimeCreated
        iic += '|' + moment.utc(this.last_activity_at).local().format();
        // invoiceNumber
        iic += '|' + this.number;
        // busiUnitCode
        iic += '|' + tenant.venue.hash;
        // tcrCode
        iic += '|' + cashRegister.hash;
        // softCode
        iic += '|' + this.$store().getters.appConfig.fiscal.softwareCode;
        // totalPrice
        iic += '|' + this.getToPay().toFormat('0.00');

        if (locale !== 'me') {
            return CryptoService.md5(iic).toUpperCase();
        }

        return CryptoService.signMessage(iic, TenantService.getPkey());
    }

    /**
     * Get IIC Code
     */
    getIic(iicSignature = null) {
        if (this.iic) {
            return this.iic;
        }

        if (!iicSignature) {
            iicSignature = this.getIicSignature();
        }

        if (locale !== 'me') {
            return iicSignature;
        }

        return CryptoService.getIIC(iicSignature);
    }

    getLinkHash() {
        const venueHash = this.$store().getters.tenant?.venue?.hash;

        if(!this.hash || this.hash.length !== 10 || !venueHash || venueHash.length !== 10) return null;

        const vh = `${venueHash[0]}${venueHash[2]}${venueHash[1]}${venueHash[3]}${venueHash[4]}` +
            `${venueHash[5]}${venueHash[7]}${venueHash[6]}${venueHash[8]}${venueHash[9]}`;

        const oh = `${this.hash[0]}${this.hash[2]}${this.hash[1]}${this.hash[3]}${this.hash[4]}` +
            `${this.hash[5]}${this.hash[7]}${this.hash[6]}${this.hash[8]}${this.hash[9]}`;

        return `${vh[0]}${vh[1]}${oh[0]}${oh[1]}${vh[2]}${vh[3]}${oh[2]}${oh[3]}${vh[4]}${vh[5]}${oh[4]}${oh[5]}${vh[6]}${vh[7]}${oh[6]}${oh[7]}${vh[8]}${vh[9]}${oh[8]}${oh[9]}`
    }

    /**
     * Get full order number
     */
    getFullNumber() {
        if (!this.$store().getters.tenant) return '';

        const issueDateTime = moment.utc(this.last_activity_at).local();

        let cashRegister = CashRegister.find(this.cash_register_id);

        return (
            this.$store().getters.tenant.venue.hash +
            '/' +
            this.number +
            '/' +
            issueDateTime.format('yy') +
            (cashRegister ? '/' + cashRegister.hash : '')
        );
    }

    allowCancel() {
        if (!!this.$store().getters.trainingMode !== !!this.isTraining()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();

        if (!curCashRegister || !curShift) return false;

        if (
            !this.cash_register_id ||
            this.correctsOthers() ||
            this.corrected() ||
            this.canceled() ||
            this.unpaidWithSummary()
        ) {
            return false;
        }

        return (
            this.isPaid() ||
            this.isUnpaid() ||
            this.isAdvance() ||
            this.isTraining() ||
            (locale === 'sr' && this.isProforma() && this.isFiscal())
        );
    }

    allowCorrect() {
        if(this.isEinvoice()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        if (!!this.$store().getters.trainingMode !== !!this.isTraining()) return false;

        if(this.isMixedPayment()) return false;

        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();

        if (!curCashRegister || !curShift) return false;

        if (
            !this.cash_register_id ||
            this.correctsOthers() ||
            this.corrected() ||
            this.canceled() ||
            this.unpaidWithSummary() ||
            this.refersToAdvance()
        ) {
            return false;
        }

        return this.isPaid();
    }

    allowCorrectSummary() {
        if (locale !== 'me') return false;

        if (!!this.$store().getters.trainingMode !== !!this.isTraining()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();

        if (!curCashRegister || !curShift) return false;

        if (!this.cash_register_id || this.correctsOthers() || this.corrected() || this.canceled() || this.unpaidWithSummary()) {
            return false;
        }

        const lastActivityAt = moment.utc(this.last_activity_at).tz('Europe/Podgorica');
        if (lastActivityAt.format('YYYY-MM-DD') !== moment().tz('Europe/Podgorica').format('YYYY-MM-DD')) {
            return false;
        }

        return this.isSummary();
    }

    allowCopy() {
        if (locale === 'me' || !this.isFiscal()) return false;

        if (!!this.$store().getters.trainingMode !== !!this.isTraining()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();

        if (!curCashRegister || !curShift) return false;

        if (!this.cash_register_id || this.corrected() || this.canceled() || this.unpaidWithSummary()) {
            return false;
        }

        if(locale === 'ba' && this.isProforma()) return false

        return this.isPaid() || this.isAdvance() || this.isTraining() || this.isProforma();
    }

    allowAdvanceNew() {
        if (locale !== 'sr' || !this.isFiscal()) return false;

        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();

        if (!!this.$store().getters.trainingMode !== !!this.isTraining()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        if (!curCashRegister || !curShift) return false;

        if (!this.cash_register_id || this.advanceClosed()) return false;

        if (this.corrected() || this.canceled()) return false;

        return this.isAdvance();
    }

    allowAdvancePay() {
        let curCashRegister = CashRegister.current();
        let curShift = Shift.current();

        if (!!this.$store().getters.trainingMode !== !!this.isTraining()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        if (!curCashRegister || !curShift) return false;

        if (!this.cash_register_id || this.advanceClosed()) return false;

        if (locale === 'sr' && !this.einvoiceIsApproved() && (!this.flagsInclude('closeAdvance') || this.getToPay().toUnit() >= 0)) return false;
        if (locale !== 'sr' && this.getToPay().toUnit() < 0) return false;

        return this.isAdvance();
    }

    allowEinvoiceFromFiscal() {
        if (!this.$store().getters.isOnline) return false;

        if (locale !== 'sr' || !this.isFiscal()) return false;

        if (!!this.$store().getters.trainingMode || !!this.isTraining()) return false;

        if(this.isIncoming()) return false;

        if(this.isExternal()) return false;

        if(!this.allowCancel()) return false;

        if(!EinvoiceService.isEnabled()) return false;

        const client = this.getClient()
        if(client?.id_type !== 'tax_id_number_local_jbkjs') return false;

        return this.isPaid();
    }

    /**
     * Get the orders that correct this order
     */
    correctedByOrders() {
        return this.referred_by.filter(order => order.reference && order.reference.type === 'corrective');
    }

    /**
     * Get the order corrected by this order
     */
    correctsOrders() {
        return this.refers_to.filter(order => order.reference && order.reference.type === 'corrective');
    }

    /**
     * Get the order that pays for this order
     */
    paidByOrder() {
        return this.referred_by.find(order => order.reference && order.reference.type === 'summary');
    }

    /**
     * Get all orders that this order pays for
     */
    paysForOrders() {
        return this.refers_to.filter(order => order.reference && order.reference.type === 'summary');
    }

    loadAllRelations() {
        this.shift = Shift.find(this.shift_hash);
        this.cashRegister = CashRegister.find(this.cash_register_id);
        this.user = User.find(this.user_id);
        this.table = Table.find(this.table_id);
        this.client = Client.find(this.client_id);

        this.items = this.items.map(item => {
            item.product = item.getProduct();
            return item;
        });

        return this;
    }

    /**
     * Format order as html
     *
     * @param {boolean} options.isReceipt is order a receipt
     * @param {boolean} options.isCopy is order a copy
     * @param {string} options.printingSector is order unpaid for a certain printing sector
     * @param {boolean} options.forceInvoiceForm force A4 form
     */
    toHtml(options) {
        return HtmlGen.orderToHtml(this, options);
    }

    /**
     * Format order as plain, monospaced text
     *
     * @param {boolean} options.isReceipt is order a receipt
     * @param {boolean} options.isCopy is order a copy
     * @param {string} options.printingSector is order unpaid for a certain printing sector
     * @param {number} lineLength number of chars in one line of text
     */
    toText(options, lineLength) {
        const pg = new PrintGen(lineLength);
        return pg.orderToText(this, options);
    }

    /**
     * Format order as encoded text
     *
     * @param {boolean} options.isReceipt is order a receipt
     * @param {boolean} options.isCopy is order a copy
     * @param {string} options.printingSector is order unpaid for a certain printing sector
     */
    toTextLines(options) {
        const pg = new PrintGen();
        return pg.orderToLines(this, options);
    }

    /**
     * Break down orders into several categories
     */
    static generateStats(orders) {
        const priceFormat = locale === 'sr' ? '0,0.00' : '0.00';

        if (locale !== 'me') {
            orders = orders.filter(order => !(order.isUnpaid() && order.canceled()));
        }

        orders = orders.filter(order => !order.isExternal());

        const unpaidOrders = orders.filter(order => order.unpaidNoSummary());
        const paidOrders = orders.filter(order => order.isMoneyExchanged());
        const paidOrdersIncludingClosedAdvance = orders.filter(order => order.isPaid() || (order.isSummary() && !order.canceled()) || order.isAdvance())

        // cash calculation
        const cash = {
            sold: paidOrders.filter(order => !order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(priceFormat),
            refunded: paidOrders.filter(order => order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(priceFormat),
        }

        // payment calculation
        const payments = {
            cash: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(priceFormat),
            credit_card: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0)).toFormat(priceFormat),
            invoice: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidInvoice()), Money.parse(0)).toFormat(priceFormat),
            credit_card_business: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCreditCardBusiness()), Money.parse(0)).toFormat(priceFormat),
            check: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidCheck()), Money.parse(0)).toFormat(priceFormat),
            voucher: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidVoucher()), Money.parse(0)).toFormat(priceFormat),
            mobile_money: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidMobileMoney()), Money.parse(0)).toFormat(priceFormat),
            other_noncash: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidOtherNoncash()), Money.parse(0)).toFormat(priceFormat),
            advance: paidOrders.reduce((acc, cur) => acc.add(cur.getPaidAdvance()), Money.parse(0)).toFormat(priceFormat),
        }

        const total = {
            sold: paidOrdersIncludingClosedAdvance.filter(order => !order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(priceFormat),
            refunded: paidOrdersIncludingClosedAdvance.filter(order => order.correctsOthers()).reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(priceFormat),
            unpaid: unpaidOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(priceFormat),
        }

        return { cash, payments, total };
    }

    /**
     * Determine whether the order should have a number
     */
    shouldHaveNumber() {
        if(locale === 'me') {
            return this.status != Order.STATUS_PROFORMA;
        }

        if(locale === 'sr') {
            if(EinvoiceService.einvoiceOnly() && this.status == Order.STATUS_PROFORMA) {
                return false;
            }

            return this.status != Order.STATUS_UNPAID;
        }

        return this.status != Order.STATUS_PROFORMA && this.status != Order.STATUS_UNPAID;
    }

    /**
     * Get last update of orders from server
     */
    static getLastUpdatedAt() {
        return Order.all().length
            ? moment(Math.max(...Order.all().filter(o => o.updated_at).map(o => moment(o.updated_at).valueOf())))
            : null;
    }

    static onTables() {
        return Order.all()
            .filter(o => o.unpaidNoSummary() && !o.corrected() && !o.correctsOthers() && !o.canceled())
    }
}

export default Order;
